import * as THREE from '../../libs/three.js/build/three.module.js';
import { DXFExporter } from '../exporter/DXFExporter.js';
import { XLSXExporter } from '../exporter/XLSXExporter.js';
import { Volume } from '../utils/Volume.js';
import { PropertiesPanel } from './PropertyPanels/PropertiesPanel.js';
import { PointCloudTree } from '../PointCloudTree.js';
import { Profile } from '../utils/Profile.js';
import { Measure } from '../utils/Measure.js';
import { Annotation } from '../Annotation.js';
import { CameraMode, ClipTask, ClipMethod } from '../defines.js';
import { Utils } from '../utils.js';
import { CameraAnimation } from '../modules/CameraAnimation/CameraAnimation.js';
import { HierarchicalSlider } from './HierarchicalSlider.js';
import { OrientedImage } from '../modules/OrientedImages/OrientedImages.js';
import { Images360 } from '../modules/Images360/index.js';

import JSON5 from '../../libs/json5-2.1.3/json5.mjs';
import {
  canAddSurface,
  plasterVolumeM3,
  volumeDecimals,
  surfaceIndexBlockers,
  surfaceIndexMissingMessage,
  surfaceLimitMessage,
} from '../utils/Surface.js';

// === Robust global search for a jstree json/node by object uuid ===
function findJsonNodeByUuid(rootId, uuid) {
  const treeSel = $('#jstree_scene');
  const jst = treeSel.jstree(true);
  if (!jst) return null;

  try {
    const direct = treeSel.jstree('get_node', uuid);
    if (direct && direct.id && direct.data && direct.data.uuid === uuid) {
      return direct;
    }
  } catch (err) {}

  try {
    const rootJson = treeSel.jstree().get_json(rootId);
    if (rootJson && Array.isArray(rootJson.children)) {
      for (const child of rootJson.children) {
        if (child && child.data && child.data.uuid === uuid) {
          const node = treeSel.jstree('get_node', child.id || child);
          if (node) return node;
        }
      }
    }
  } catch (err) {}

  try {
    const rootNode = treeSel.jstree('get_node', rootId);
    if (rootNode && rootNode.children_d && rootNode.children_d.length) {
      for (const key of rootNode.children_d) {
        const n = treeSel.jstree('get_node', key);
        if (n && n.data && n.data.uuid === uuid) return n;
      }
    }
  } catch (err) {}

  try {
    const all = treeSel.jstree().get_json('#');
    const flat = Array.isArray(all) ? all : [all];
    const stack = flat.slice();
    while (stack.length) {
      const nodeJson = stack.shift();
      if (nodeJson) {
        if (nodeJson.data && nodeJson.data.uuid === uuid) {
          return treeSel.jstree('get_node', nodeJson.id || nodeJson);
        }
        if (Array.isArray(nodeJson.children) && nodeJson.children.length) {
          stack.push(...nodeJson.children);
        }
      }
    }
  } catch (err) {}

  return null;
}

export class Sidebar {
  static PANEL_MIN_WIDTH = 260;
  static PANEL_MAX_WIDTH = 720;

  constructor(viewer) {
    this.viewer = viewer;

    this.measuringTool = viewer.measuringTool;
    this.profileTool = viewer.profileTool;
    this.volumeTool = viewer.volumeTool;
    this._folders = 0; //TODO: load folders from loaded file
    this.foldersAnnotationMap = new Map();
    this.foldersCameraAMap = new Map();
    this.foldersMeasureMap = new Map();

    this.dom = $('#sidebar_root');

    // Shared panel state: all right panels share a single pin toggle and
    // behave as one logical panel slot (opening one closes the others).
    this._panelPin = false;
    this._panelRegistry = [];  // { overlay, close(keepPin), pinBtn }
  }

  /** Register a right-side panel so it participates in the shared pin / mutual-exclusion system. */
  _registerPanel(overlay, closeFn, pinBtn) {
    const entry = { overlay, close: closeFn, pinBtn };
    this._panelRegistry.push(entry);
    if (pinBtn) {
      pinBtn.addEventListener('click', () => {
        this._panelPin = !this._panelPin;
        overlay.classList.toggle('is-pinned', this._panelPin);
        pinBtn.classList.toggle('active', this._panelPin);
      });
    }

    this._initPanelResize(overlay);

    // Overlays that reach into the canvas (the panorama exit button) need to
    // know when a panel takes up the right edge.
    new MutationObserver(() => {
      if (overlay.classList.contains('is-open')) {
        this._notifyPanelWidth();
      } else {
        this._notifyPanelWidth(true);
      }
    }).observe(overlay, { attributes: true, attributeFilter: ['class'] });
  }

  /** Drag the left edge of a panel to resize it. Width is shared by all panels. */
  _initPanelResize(overlay) {
    const dialog = overlay.querySelector('.potree-dialog');
    if (!dialog || dialog.querySelector('.potree-dialog-resize-handle')) return;

    const stored = Number(localStorage.getItem('potree.panelWidth'));
    if (stored >= Sidebar.PANEL_MIN_WIDTH && stored <= Sidebar.PANEL_MAX_WIDTH) {
      document.documentElement.style.setProperty('--potree-panel-width', `${stored}px`);
    }

    const handle = document.createElement('div');
    handle.className = 'potree-dialog-resize-handle';
    dialog.prepend(handle);

    let startX = 0;
    let startWidth = 0;

    const onMove = (e) => {
      // Panels are anchored to the right edge, so dragging left widens them.
      const width = Math.min(
        Sidebar.PANEL_MAX_WIDTH,
        Math.max(Sidebar.PANEL_MIN_WIDTH, startWidth + (startX - e.clientX))
      );
      document.documentElement.style.setProperty('--potree-panel-width', `${width}px`);
      this._notifyPanelWidth();
    };

    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.classList.remove('potree-panel-resizing');
      localStorage.setItem('potree.panelWidth', String(Math.round(dialog.getBoundingClientRect().width)));
    };

    handle.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      startX = e.clientX;
      startWidth = dialog.getBoundingClientRect().width;
      document.body.classList.add('potree-panel-resizing');
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    });
  }

  /**
   * Publish how much of the right edge is covered, as a CSS variable plus an
   * event, so canvas-level controls can move out of the way.
   */
  _notifyPanelWidth(maybeClosed = false) {
    if (maybeClosed && !this._panelRegistry.some((p) => p.overlay.classList.contains('is-open'))) {
      document.documentElement.style.setProperty('--potree-right-panel-offset', '0px');
      window.dispatchEvent(new CustomEvent('potree-panel-width-changed', { detail: { width: 0 } }));
      return;
    }

    const open = this._panelRegistry.find((p) => p.overlay.classList.contains('is-open'));
    if (!open) return;

    const dialog = open.overlay.querySelector('.potree-dialog');
    const width = dialog ? Math.round(dialog.getBoundingClientRect().width) : 0;

    document.documentElement.style.setProperty('--potree-right-panel-offset', `${width}px`);
    window.dispatchEvent(new CustomEvent('potree-panel-width-changed', { detail: { width } }));
  }

  /** Show a styled confirmation dialog. Returns a Promise<boolean>. */
  _confirm(message) {
    return new Promise((resolve) => {
      if (!this._confirmOverlay) {
        const overlay = document.createElement('div');
        overlay.className = 'potree-confirm-overlay';
        overlay.innerHTML = `
          <div class="potree-confirm-dialog">
            <div class="confirm-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <div class="confirm-message"></div>
            <div class="confirm-actions">
              <button class="confirm-btn confirm-btn-cancel" data-i18n="tt.confirm_cancel">Cancel</button>
              <button class="confirm-btn confirm-btn-delete" data-i18n="tt.confirm_delete">Delete</button>
            </div>
          </div>
        `;
        document.body.appendChild(overlay);
        this._confirmOverlay = overlay;
      }
      const overlay = this._confirmOverlay;
      overlay.querySelector('.confirm-message').textContent = message;
      $(overlay).i18n();
      // Force reflow to ensure transition plays
      overlay.classList.remove('is-open');
      void overlay.offsetWidth;
      overlay.classList.add('is-open');

      const cleanup = (result) => {
        overlay.classList.remove('is-open');
        cancelBtn.removeEventListener('click', onCancel);
        deleteBtn.removeEventListener('click', onDelete);
        overlay.removeEventListener('click', onBackdrop);
        document.removeEventListener('keydown', onKey);
        resolve(result);
      };
      const onCancel = () => cleanup(false);
      const onDelete = () => cleanup(true);
      const onBackdrop = (e) => { if (e.target === overlay) cleanup(false); };
      const onKey = (e) => { if (e.key === 'Escape') cleanup(false); };

      const cancelBtn = overlay.querySelector('.confirm-btn-cancel');
      const deleteBtn = overlay.querySelector('.confirm-btn-delete');
      cancelBtn.addEventListener('click', onCancel);
      deleteBtn.addEventListener('click', onDelete);
      overlay.addEventListener('click', onBackdrop);
      document.addEventListener('keydown', onKey);
    });
  }

  /** Close every other panel before opening `overlay`. Preserves shared pin state. */
  _beforeOpenPanel(overlay) {
    for (const p of this._panelRegistry) {
      if (p.overlay !== overlay && p.overlay.classList.contains('is-open')) {
        p.close(true); // keepPin = true
      }
    }
    // Apply shared pin state to the panel about to open
    const entry = this._panelRegistry.find(p => p.overlay === overlay);
    if (entry && entry.pinBtn) {
      overlay.classList.toggle('is-pinned', this._panelPin);
      entry.pinBtn.classList.toggle('active', this._panelPin);
    }
  }

  _clearActiveTools() {
    document.querySelectorAll('.tool-btn.active, .button-icon.active')
      .forEach(el => el.classList.remove('active'));
    this._hideToolHint();
  }

  _hideToolHint() {
    if (this._toolHintEl) {
      this._toolHintEl.classList.remove('is-visible');
    }
    if (this._toolHintTimer) {
      clearTimeout(this._toolHintTimer);
      this._toolHintTimer = null;
    }
  }

  createToolIcon(icon, title, callback) {
    const cacheBust = (icon.startsWith('data:') || icon.includes('?')) ? '' : '?v=2';
    let element = $(`
			<img src="${icon}${cacheBust}"
				style="width: 32px; height: 32px"
				class="button-icon"
				data-i18n="${title}" />
		`);

    element.click(callback);

    return element;
  }

  createToolButton(icon, title, callback) {
    let btn = document.createElement('button');
    btn.className = 'tool-btn';
    btn.title = title;
    let img = document.createElement('img');
    img.src = icon;
    img.alt = title;
    btn.appendChild(img);
    btn.addEventListener('click', callback);
    return btn;
  }

  _exportMeasurements(items, format) {
    if (!items || items.length === 0) { this.viewer.postError('no measurements to export'); return; }
    let blob, filename;
    try {
      if (format === 'dxf') {
        blob = new Blob([DXFExporter.toString(items, this.viewer.measurementDisplayOffset)], { type: 'application/octet-stream' });
        filename = 'measure.dxf';
      } else if (format === 'xlsx') {
        blob = XLSXExporter.toBlob(items, this.viewer.measurementDisplayOffset);
        filename = 'measure.xlsx';
      } else {
        return;
      }
    } catch (err) {
      console.error('measurement export failed', err);
      this.viewer.postError(`export failed: ${err.message || err}`);
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    window.URL.revokeObjectURL(url);
  }

  initTopbar() {
    const viewer = this.viewer;

    const actions = {
      'fit-to-screen':          () => viewer.fitToScreen(),
      'bg-gradient':            () => viewer.setBackground('gradient'),
      'bg-skybox':              () => viewer.setBackground('skybox'),
      'bg-black':               () => viewer.setBackground('black'),
      'bg-white':               () => viewer.setBackground('white'),
      'bg-none':                () => viewer.setBackground(null),
      'toggle-sidebar':         () => {
        let sc = document.getElementById('potree_sidebar_container');
        if (sc) sc.style.display = sc.style.display === 'none' ? '' : 'none';
      },
      'measure-distance':       () => this.measuringTool.startInsertion({ showDistances: true, showTotalDistance: true, closed: false, name: i18n.t('measurements.name_distance', 'Distance') }),
      'measure-height':         () => this.measuringTool.startInsertion({ showDistances: false, showHeight: true, closed: false, maxMarkers: 2, name: i18n.t('measurements.name_height', 'Height') }),
      'measure-angle':          () => this.measuringTool.startInsertion({ showDistances: false, showAngles: true, closed: true, maxMarkers: 3, name: i18n.t('measurements.name_angle', 'Angle') }),
      'measure-area':           () => this.measuringTool.startInsertion({ showDistances: true, showArea: true, closed: true, name: i18n.t('measurements.name_area', 'Area') }),
      'measure-point':          () => this.measuringTool.startInsertion({ showDistances: false, showCoordinates: true, closed: true, maxMarkers: 1, name: i18n.t('measurements.name_point', 'Point') }),
      'measure-profile':        () => this.profileTool.startInsertion({ name: i18n.t('measurements.name_profile', 'Profile') }),
      'measure-azimuth':        () => {
        this.measuringTool.startInsertion({ showDistances: false, showHeight: false, showArea: false, showCircle: false, showEdges: false, showAzimuth: true, closed: false, maxMarkers: 2, name: i18n.t('measurements.name_azimuth', 'Azimuth') });
        this._showToolHint('azimuth');
      },
      'tool-camera-position':   () => {
        this.viewer.annotationTool.insertCameraPosition();
        this._showToolHint('camera_annotation');
      },
      'tool-camera-animation':  () => {
        const animation = CameraAnimation.defaultFromView(this.viewer);
        this.viewer.scene.addCameraAnimation(animation);
        this._contentPanels['open-camera-animations-panel'](animation);
        this._showToolHint('camera_animation', { instant: true });
      },
      'measure-remove-all':     () => viewer.scene.removeAllMeasurements(),
      'clip-none':              () => viewer.setClipTask(ClipTask.NONE),
      'clip-highlight':         () => viewer.setClipTask(ClipTask.HIGHLIGHT),
      'clip-inside':            () => viewer.setClipTask(ClipTask.SHOW_INSIDE),
      'clip-outside':           () => viewer.setClipTask(ClipTask.SHOW_OUTSIDE),
      'nav-orbit':              () => viewer.setControls(viewer.orbitControls),
      'nav-earth':              () => viewer.setControls(viewer.earthControls),
      'nav-fps':                () => viewer.setControls(viewer.fpControls),
      'open-appearance-dialog': () => this._openAppearancePanel && this._openAppearancePanel(),
      'open-clipping-panel':    () => this._openClippingPanel && this._openClippingPanel(),
      'open-classification-panel': () => this._openClassificationPanel(),
      'theme-light': () => {
        document.documentElement.removeAttribute('data-theme');
        localStorage.setItem('potree-theme', 'light');
        localStorage.setItem('potree-background', 'gradient-light');
        viewer.setBackground('gradient-light');
        updateTopbarChecks();
      },
      'theme-dark': () => {
        document.documentElement.setAttribute('data-theme', 'dark');
        localStorage.setItem('potree-theme', 'dark');
        localStorage.setItem('potree-background', 'gradient');
        viewer.setBackground('gradient');
        updateTopbarChecks();
      },
      'lang-en': () => { viewer.setLanguage('en'); localStorage.setItem('potree-language', 'en'); updateTopbarChecks(); },
      'lang-pl': () => { viewer.setLanguage('pl'); localStorage.setItem('potree-language', 'pl'); updateTopbarChecks(); },
      'tool-hints-on': () => { localStorage.setItem('potree-tool-hints', 'on'); updateTopbarChecks(); },
      'tool-hints-off': () => { localStorage.setItem('potree-tool-hints', 'off'); updateTopbarChecks(); },
      'open-pointclouds-panel':          () => this._contentPanels['open-pointclouds-panel'](),
      'toggle-map':                      () => viewer.toggleMap(),
      'open-measurements-panel':         () => this._contentPanels['open-measurements-panel'](),
      'open-annotations-panel':          () => this._contentPanels['open-annotations-panel'](),
      'open-camera-annotations-panel':   () => this._contentPanels['open-camera-annotations-panel'](),
      'open-other-panel':                () => this._contentPanels['open-other-panel'](),
      'open-vectors-panel':              () => this._contentPanels['open-vectors-panel'](),
      'open-images-panel':               () => this._contentPanels['open-images-panel'](),
      'open-panorama-edit-panel':        () => this._contentPanels['open-panorama-edit-panel']?.(),
    };

    let currentLang = localStorage.getItem('potree-language') || 'en';

    const updateTopbarChecks = () => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      document.querySelectorAll('#potree_topbar [data-action="theme-light"], #potree_topbar [data-action="theme-dark"]').forEach(el => {
        el.classList.toggle('is-checked', el.dataset.action === (isDark ? 'theme-dark' : 'theme-light'));
      });
      document.querySelectorAll('#potree_topbar [data-action="lang-en"], #potree_topbar [data-action="lang-pl"]').forEach(el => {
        el.classList.toggle('is-checked', el.dataset.action === 'lang-' + currentLang);
      });
      const hintsOn = localStorage.getItem('potree-tool-hints') !== 'off';
      document.querySelectorAll('#potree_topbar [data-action="tool-hints-on"], #potree_topbar [data-action="tool-hints-off"]').forEach(el => {
        el.classList.toggle('is-checked', el.dataset.action === (hintsOn ? 'tool-hints-on' : 'tool-hints-off'));
      });
    };

    const origLangEn = actions['lang-en'];
    const origLangPl = actions['lang-pl'];
    actions['lang-en'] = () => { currentLang = 'en'; origLangEn(); };
    actions['lang-pl'] = () => { currentLang = 'pl'; origLangPl(); };

    const wireActions = () => {
      document.querySelectorAll('#potree_topbar .topbar-action').forEach(el => {
        // Replace node to drop any listeners from a previous remount race.
        const fresh = el.cloneNode(true);
        el.replaceWith(fresh);
        fresh.addEventListener('click', () => {
          const action = fresh.dataset.action;
          if (actions[action]) actions[action]();
        });
      });
    };

    const mountTopbar = (html) => {
      // Exactly one topbar — drop orphans from StrictMode / remount races.
      document.querySelectorAll('#potree_topbar').forEach((el) => el.remove());

      const div = document.createElement('div');
      div.innerHTML = html.trim();
      const topbar = div.firstElementChild;
      if (!topbar) return;
      document.body.appendChild(topbar);
      wireActions();
      $('#potree_topbar').i18n();
      updateTopbarChecks();
      const logoLight = document.getElementById('potree_logo_light');
      const logoDark = document.getElementById('potree_logo_dark');
      if (logoLight) logoLight.src = Potree.resourcePath + '/noach-logo-light.png';
      if (logoDark) logoDark.src = Potree.resourcePath + '/noach-logo-dark.png';

      if (viewer.hideThemeAndLanguage) {
        const themeItem = document.querySelector('#potree_topbar [data-action="theme-light"]')?.closest('.topbar-sub');
        const langItem  = document.querySelector('#potree_topbar [data-action="lang-en"]')?.closest('.topbar-sub');
        if (themeItem) themeItem.style.display = 'none';
        if (langItem)  langItem.style.display  = 'none';
      }
    };

    // Generation token: PotreeViewer unmount / remount bumps this so a late
    // $.get from a disposed Sidebar cannot append (or strip) the live topbar.
    this._topbarLoadId = (this._topbarLoadId || 0) + 1;
    const loadId = this._topbarLoadId;

    $.get(new URL(`${Potree.scriptPath}/topbar.html?v=${encodeURIComponent(Potree.assetCacheKey)}`).href, (html) => {
      if (loadId !== this._topbarLoadId) return;
      mountTopbar(html);
    }).fail(() => {
      console.warn('[Potree] failed to load topbar.html');
    });
  }

  /** Invalidate in-flight topbar.html fetch (call before removing #potree_topbar). */
  cancelTopbarLoad() {
    this._topbarLoadId = (this._topbarLoadId || 0) + 1;
  }

  /** Re-fetch topbar if a remount race left the page without one. */
  ensureTopbar() {
    if (document.getElementById('potree_topbar')) return;
    this.initTopbar();
  }

  init() {
    this.viewer.addEventListener('cancel_insertions', () => this._clearActiveTools());
    this.viewer.addEventListener('measurement_completed', (e) => {
      this._clearActiveTools();
      if (e.measurement) {
        this._contentPanels['open-measurements-panel'](e.measurement);
      }
    });
    this.viewer.addEventListener('annotation_completed', (e) => {
      this._clearActiveTools();
      if (e.annotation) {
        const panel = e.annotation.cameraPosition
          ? this._contentPanels['open-camera-annotations-panel']
          : this._contentPanels['open-annotations-panel'];
        if (panel) panel(e.annotation);
      }
    });

    // Catch Escape and RMB at document level (capture phase) so they always
    // deactivate the active tool button regardless of canvas focus state.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._clearActiveTools();
    }, true);
    this.viewer.renderer.domElement.addEventListener('mouseup', (e) => {
      if (e.button === 2) this._clearActiveTools();
    }, true);

    this.initToolbar();
    this.initSettings();
    this.initTopbar();
    this.initAppearanceDialog();
    this.initContentPanels();
    this.initClassificationPanel();

  }

  initAppearanceDialog() {
    const overlay = document.getElementById('appearance-dialog-overlay');
    const viewer = this.viewer;

    // Move to body so it escapes the sidebar's z-index stacking context
    document.body.appendChild(overlay);
    overlay.classList.add('no-backdrop');

    // Close / pin handlers
    const pinBtn = document.getElementById('appearance-dialog-pin');
    let justOpened = false;

    const closePanel = (keepPin = false) => {
      if (!keepPin) this._panelPin = false;
      overlay.classList.remove('is-open', 'is-pinned');
      if (pinBtn) pinBtn.classList.remove('active');
    };

    this._registerPanel(overlay, closePanel, pinBtn);

    document.getElementById('appearance-dialog-close').addEventListener('click', () => closePanel());
    document.addEventListener('click', (e) => {
      if (!overlay.classList.contains('is-open')) return;
      if (justOpened) return;
      if (this._panelPin) return;
      if (overlay.querySelector('.potree-dialog').contains(e.target)) return;
      closePanel();
    }, true);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closePanel();
    });

    // Restore saved appearance settings.
    // Point budget is a per-BROWSER preference: persisted in localStorage and
    // restored here. It must never be part of a saved view state (see
    // ViewState.js) — a view is shared across machines, and a budget captured
    // on one machine would override every other machine's preference.
    const ls = localStorage;
    const savedPB  = ls.getItem('potree-point-budget');
    const savedFOV = ls.getItem('potree-fov');
    const savedEDLEnabled  = ls.getItem('potree-edl-enabled');
    const savedEDLRadius   = ls.getItem('potree-edl-radius');
    const savedEDLStrength = ls.getItem('potree-edl-strength');
    const savedEDLOpacity  = ls.getItem('potree-edl-opacity');
    const savedMeshOpacity = ls.getItem('potree-mesh-opacity');
    const savedBimOpacity  = ls.getItem('potree-bim-opacity');
    const savedBg          = ls.getItem('potree-background');
    const savedMNS         = ls.getItem('potree-min-node-size');

    if (savedPB)          viewer.setPointBudget(parseInt(savedPB));
    if (savedFOV)         viewer.setFOV(parseFloat(savedFOV));
    if (savedEDLEnabled !== null) viewer.setEDLEnabled(savedEDLEnabled === 'true');
    if (savedEDLRadius)   viewer.setEDLRadius(parseFloat(savedEDLRadius));
    if (savedEDLStrength) viewer.setEDLStrength(parseFloat(savedEDLStrength));
    if (savedEDLOpacity)  viewer.setEDLOpacity(parseFloat(savedEDLOpacity));
    if (savedMeshOpacity) viewer.setMeshOpacity(parseFloat(savedMeshOpacity));
    else if (savedEDLOpacity) viewer.setMeshOpacity(parseFloat(savedEDLOpacity));
    if (savedBimOpacity)  viewer.setBimOpacity(parseFloat(savedBimOpacity));
    else if (savedEDLOpacity) viewer.setBimOpacity(parseFloat(savedEDLOpacity));
    if (savedBg)          viewer.setBackground(savedBg === 'null' ? null : savedBg);
    if (savedMNS)         viewer.setMinNodeSize(parseFloat(savedMNS));

    // Splat quality is fixed to standard (default); not exposed in Appearance.
    viewer.useHQ = false;
    ls.removeItem('potree-splat-quality');

    // Point Budget
    const sldPB = $('#dlg_sldPointBudget');
    sldPB.slider({
      value: viewer.getPointBudget(), min: 100_000, max: 10_000_000, step: 1000,
      slide: (e, ui) => { viewer.setPointBudget(ui.value); ls.setItem('potree-point-budget', ui.value); },
    });
    viewer.addEventListener('point_budget_changed', () => {
      $('#dlg_lblPointBudget')[0].innerHTML = Utils.addCommas(viewer.getPointBudget());
      sldPB.slider({ value: viewer.getPointBudget() });
    });
    $('#dlg_lblPointBudget')[0].innerHTML = Utils.addCommas(viewer.getPointBudget());

    // FOV
    const sldFOV = $('#dlg_sldFOV');
    sldFOV.slider({
      value: viewer.getFOV(), min: 20, max: 100, step: 1,
      slide: (e, ui) => { viewer.setFOV(ui.value); ls.setItem('potree-fov', ui.value); },
    });
    viewer.addEventListener('fov_changed', () => {
      $('#dlg_lblFOV')[0].innerHTML = parseInt(viewer.getFOV());
      sldFOV.slider({ value: viewer.getFOV() });
    });
    $('#dlg_lblFOV')[0].innerHTML = parseInt(viewer.getFOV());

    // EDL
    const sldEDLR = $('#dlg_sldEDLRadius');
    sldEDLR.slider({
      value: viewer.getEDLRadius(), min: 1, max: 4, step: 0.01,
      slide: (e, ui) => { viewer.setEDLRadius(ui.value); ls.setItem('potree-edl-radius', ui.value); },
    });
    viewer.addEventListener('edl_radius_changed', () => {
      $('#dlg_lblEDLRadius')[0].innerHTML = viewer.getEDLRadius().toFixed(1);
      sldEDLR.slider({ value: viewer.getEDLRadius() });
    });
    $('#dlg_lblEDLRadius')[0].innerHTML = viewer.getEDLRadius().toFixed(1);

    const sldEDLS = $('#dlg_sldEDLStrength');
    sldEDLS.slider({
      value: viewer.getEDLStrength(), min: 0, max: 5, step: 0.01,
      slide: (e, ui) => { viewer.setEDLStrength(ui.value); ls.setItem('potree-edl-strength', ui.value); },
    });
    viewer.addEventListener('edl_strength_changed', () => {
      $('#dlg_lblEDLStrength')[0].innerHTML = viewer.getEDLStrength().toFixed(1);
      sldEDLS.slider({ value: viewer.getEDLStrength() });
    });
    $('#dlg_lblEDLStrength')[0].innerHTML = viewer.getEDLStrength().toFixed(1);

    const bindOpacitySlider = (sldSel, lblSel, getVal, setVal, eventName, lsKey) => {
      const sld = $(sldSel);
      const lbl = $(lblSel);
      if (!sld.length || !lbl.length) return;
      sld.slider({
        value: getVal(), min: 0, max: 1, step: 0.01,
        slide: (e, ui) => { setVal(ui.value); ls.setItem(lsKey, ui.value); },
      });
      lbl[0].innerHTML = getVal().toFixed(2);
      viewer.addEventListener(eventName, () => {
        const v = getVal();
        sld.slider({ value: v });
        lbl[0].innerHTML = v.toFixed(2);
      });
    };

    bindOpacitySlider(
      '#dlg_sldCloudOpacity', '#dlg_lblCloudOpacity',
      () => viewer.getEDLOpacity(),
      (v) => viewer.setEDLOpacity(v),
      'edl_opacity_changed',
      'potree-edl-opacity',
    );
    bindOpacitySlider(
      '#dlg_sldMeshOpacity', '#dlg_lblMeshOpacity',
      () => viewer.getMeshOpacity(),
      (v) => viewer.setMeshOpacity(v),
      'mesh_opacity_changed',
      'potree-mesh-opacity',
    );
    bindOpacitySlider(
      '#dlg_sldBimOpacity', '#dlg_lblBimOpacity',
      () => viewer.getBimOpacity(),
      (v) => viewer.setBimOpacity(v),
      'bim_opacity_changed',
      'potree-bim-opacity',
    );

    const isBimItem = (item) => !!(item && item.userData && item.userData.isBimModel);
    const syncOpacityRows = () => {
      const scene = viewer.scene;
      const hasClouds = !!(scene && scene.pointclouds && scene.pointclouds.length > 0);
      const items = (scene && scene.realityMeshes) || [];
      let hasMeshes = false;
      let hasBim = false;
      for (const item of items) {
        if (isBimItem(item)) hasBim = true;
        else hasMeshes = true;
      }
      const any = hasClouds || hasMeshes || hasBim;
      const section = document.getElementById('dlg_opacity_section');
      const cloudLi = document.getElementById('dlg_opacity_clouds_li');
      const meshLi = document.getElementById('dlg_opacity_meshes_li');
      const bimLi = document.getElementById('dlg_opacity_bim_li');
      if (section) section.style.display = any ? '' : 'none';
      if (cloudLi) cloudLi.style.display = hasClouds ? '' : 'none';
      if (meshLi) meshLi.style.display = hasMeshes ? '' : 'none';
      if (bimLi) bimLi.style.display = hasBim ? '' : 'none';
    };
    const listenSceneOpacity = (scene) => {
      if (!scene || typeof scene.addEventListener !== 'function') return;
      scene.addEventListener('pointcloud_added', syncOpacityRows);
      scene.addEventListener('reality_mesh_added', syncOpacityRows);
      scene.addEventListener('reality_mesh_removed', syncOpacityRows);
    };
    listenSceneOpacity(viewer.scene);
    viewer.addEventListener('scene_changed', (e) => {
      listenSceneOpacity(e.scene);
      syncOpacityRows();
    });
    syncOpacityRows();

    $('#dlg_chkEDLEnabled')[0].checked = viewer.getEDLEnabled();
    if (typeof viewer.isEDLSupported === 'function' && !viewer.isEDLSupported()) {
      $('#dlg_chkEDLEnabled').prop('disabled', true);
    }
    viewer.addEventListener('use_edl_changed', () => {
      $('#dlg_chkEDLEnabled')[0].checked = viewer.getEDLEnabled();
    });
    $('#dlg_chkEDLEnabled').click(() => {
      const v = $('#dlg_chkEDLEnabled').prop('checked');
      viewer.setEDLEnabled(v);
      ls.setItem('potree-edl-enabled', v);
    });

    // Background
    const elBg = $('#dlg_background_options');
    elBg.val(viewer.getBackground() ?? 'null');
    elBg.change(() => {
      const val = elBg.val();
      viewer.setBackground(val === 'null' ? null : val);
      ls.setItem('potree-background', val);
    });
    viewer.addEventListener('background_changed', () => {
      elBg.val(viewer.getBackground() ?? 'null');
    });

    // Min Node Size
    const sldMNS = $('#dlg_sldMinNodeSize');
    sldMNS.slider({
      value: viewer.getMinNodeSize(), min: 0, max: 1000, step: 0.01,
      slide: (e, ui) => { viewer.setMinNodeSize(ui.value); ls.setItem('potree-min-node-size', ui.value); },
    });
    viewer.addEventListener('minnodesize_changed', () => {
      $('#dlg_lblMinNodeSize').html(parseInt(viewer.getMinNodeSize()));
      sldMNS.slider({ value: viewer.getMinNodeSize() });
    });
    $('#dlg_lblMinNodeSize').html(parseInt(viewer.getMinNodeSize()));

    // Move speed
    const sldContainer = $('#dlg_sldMoveSpeed')[0];
    const sldEl = document.createElement('input');
    sldEl.type = 'range';
    sldEl.min = '0';
    sldEl.max = '1';
    sldEl.step = '0.01';
    const speedRange = new THREE.Vector2(1, 10 * 1000);
    const toLinearSpeed = (v) => Math.pow(v, 4) * speedRange.y + speedRange.x;
    const toExpSpeed    = (v) => Math.pow((v - speedRange.x) / speedRange.y, 1 / 4);
    sldEl.value = toExpSpeed(viewer.getMoveSpeed());
    sldContainer.replaceWith(sldEl);
    const lblMoveSpeed = $('#dlg_lblMoveSpeed');
    let dragging = false;
    sldEl.addEventListener('pointerdown', () => { dragging = true; });
    sldEl.addEventListener('pointerup', () => { dragging = false; });
    sldEl.addEventListener('wheel', (e) => { e.preventDefault(); sldEl.blur(); }, { passive: false });
    sldEl.addEventListener('input', () => {
      viewer.setMoveSpeed(toLinearSpeed(parseFloat(sldEl.value)));
      lblMoveSpeed.html(viewer.getMoveSpeed().toFixed(1));
    });
    viewer.addEventListener('move_speed_changed', () => {
      lblMoveSpeed.html(viewer.getMoveSpeed().toFixed(1));
      if (!dragging) sldEl.value = toExpSpeed(viewer.getMoveSpeed());
    });
    lblMoveSpeed.html(viewer.getMoveSpeed().toFixed(1));

    const syncSpeedEnabled = () => {
      const isFP = viewer.controls !== viewer.orbitControls;
      sldEl.disabled = !isFP;
      sldEl.style.opacity = isFP ? '1' : '0.4';
    };
    syncSpeedEnabled();

    // Control mode
    const elControlMode = $('#dlg_control_mode');
    const syncControlMode = () => {
      const c = viewer.controls;
      if (c === viewer.orbitControls) elControlMode.val('orbit');
      else if (c === viewer.earthControls) elControlMode.val('earth');
      else if (c === viewer.fpControls && viewer.fpControls.lockElevation) elControlMode.val('helicopter');
      else if (c === viewer.fpControls) elControlMode.val('flight');
    };
    syncControlMode();
    elControlMode.change(() => {
      const v = elControlMode.val();
      if (v === 'orbit')      { viewer.setControls(viewer.orbitControls); }
      else if (v === 'earth') { viewer.setControls(viewer.earthControls); }
      else if (v === 'flight')      { viewer.setControls(viewer.fpControls); viewer.fpControls.lockElevation = false; }
      else if (v === 'helicopter')  { viewer.setControls(viewer.fpControls); viewer.fpControls.lockElevation = true; }
      syncSpeedEnabled();
    });
    new MutationObserver(() => {
      if (overlay.classList.contains('is-open')) {
        syncControlMode();
        syncSpeedEnabled();
        syncCamProj();
        $('#dlg_show_compass').prop('checked', viewer.compass.isVisible());
      }
    }).observe(overlay, { attributes: true, attributeFilter: ['class'] });

    // Compass rose
    const savedCompass = localStorage.getItem('potree-show-compass');
    if (savedCompass !== null) {
      viewer.compass.setVisible(savedCompass === 'true');
    }
    $('#dlg_show_compass').prop('checked', viewer.compass.isVisible());
    $('#dlg_show_compass').click(() => {
      const visible = $('#dlg_show_compass').prop('checked');
      viewer.compass.setVisible(visible);
      localStorage.setItem('potree-show-compass', String(visible));
    });

    // Camera projection
    const elCamProj = $('#dlg_camera_projection');
    const syncCamProj = () => {
      const key = Object.keys(CameraMode).find(k => CameraMode[k] === viewer.scene.cameraMode);
      if (key) elCamProj.val(key);
    };
    syncCamProj();
    elCamProj.change(() => {
      const mode = CameraMode[elCamProj.val()];
      viewer.setCameraMode(mode);
      if (mode === CameraMode.ORTHOGRAPHIC) {
        for (const pc of viewer.scene.pointclouds) {
          if (!pc || !pc.material) continue;
          if (pc._prevShape === undefined) pc._prevShape = pc.material.shape;
          pc.material.shape = Potree.PointShape.CIRCLE;
          pc.material.needsUpdate = true;
        }
      } else if (mode === CameraMode.PERSPECTIVE) {
        for (const pc of viewer.scene.pointclouds) {
          if (!pc || !pc.material) continue;
          if (pc._prevShape !== undefined) {
            pc.material.shape = pc._prevShape;
            delete pc._prevShape;
            pc.material.needsUpdate = true;
          }
        }
      }
    });

    // Bounding box & freeze
    $('#dlg_show_bounding_box').prop('checked', viewer.getShowBoundingBox());
    $('#dlg_show_bounding_box').click(() => {
      viewer.setShowBoundingBox($('#dlg_show_bounding_box').prop('checked'));
    });
    viewer.addEventListener('show_boundingbox_changed', () => {
      $('#dlg_show_bounding_box').prop('checked', viewer.getShowBoundingBox());
    });

    $('#dlg_set_freeze').prop('checked', viewer.freeze);
    $('#dlg_set_freeze').click(() => {
      viewer.setFreeze($('#dlg_set_freeze').prop('checked'));
    });

    // Panorama sphere size (hidden until panoramas load)
    const sldSS = $('#dlg_sldSphereSize');
    if (sldSS.length) {
      sldSS.slider({
        value: 0.2, min: 0.03, max: 2, step: 0.01,
        slide: (e, ui) => {
          $('#dlg_lblSphereSize').text(parseFloat(ui.value).toFixed(2));
          viewer.dispatchEvent({
            type: 'panorama_sphere_size_changed',
            value: parseFloat(ui.value),
          });
        },
      });
      $('#dlg_lblSphereSize').text('0.20');
    }

    // Panorama render distance (hidden until panoramas load)
    const RD_MIN = 0;
    const RD_MAX = 300;
    const RD_STEP = 0.5;
    const sldRD = $('#dlg_sldRenderDistance');
    const inpRD = $('#dlg_inpRenderDistance');
    if (sldRD.length) {
      const applyRenderDistance = (raw, { fromSlider = false } = {}) => {
        let v = parseFloat(raw);
        if (!Number.isFinite(v)) {
          if (inpRD.length) {
            inpRD.val(sldRD.slider('value'));
          }
          return;
        }
        v = Math.min(RD_MAX, Math.max(RD_MIN, v));
        v = Math.round(v / RD_STEP) * RD_STEP;
        if (inpRD.length) inpRD.val(v);
        if (!fromSlider) sldRD.slider('value', v);
        viewer.dispatchEvent({
          type: 'panorama_render_distance_changed',
          value: v,
        });
      };

      sldRD.slider({
        value: 150, min: RD_MIN, max: RD_MAX, step: RD_STEP,
        slide: (e, ui) => applyRenderDistance(ui.value, { fromSlider: true }),
      });
      if (inpRD.length) {
        inpRD.val(150);
        inpRD.on('change', () => applyRenderDistance(inpRD.val()));
        inpRD.on('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            applyRenderDistance(inpRD.val());
            inpRD.blur();
          }
        });
      }
    }

    // Show panorama section when 360 images are added
    viewer.scene.addEventListener('360_images_added', () => {
      const section = document.getElementById('dlg_panorama_section');
      const ssLi = document.getElementById('dlg_panorama_sphere_size_li');
      const rdLi = document.getElementById('dlg_panorama_render_distance_li');
      if (section) section.style.display = '';
      if (ssLi) ssLi.style.display = '';
      if (rdLi) rdLi.style.display = '';
    });

    this._openAppearancePanel = () => {
      this._beforeOpenPanel(overlay);
      $(overlay).i18n();
      syncOpacityRows();
      overlay.classList.add('is-open');
      justOpened = true;
      setTimeout(() => { justOpened = false; }, 0);
    };
  }

  // ── Generic accordion panel builder ──
  _buildAccordionPanel(overlayId, titleText, titleI18nKey, sectionConfigs, options = {}) {
    // Build single overlay with accordion sections
    const overlay = document.createElement('div');
    overlay.id = overlayId;
    overlay.className = 'potree-dialog-overlay no-backdrop';
    const snapBtnHtml = options.snapSettings
      ? `<button type="button" class="potree-dialog-snap" aria-label="Measurement snap settings" title="Measurement snap settings" data-i18n="[title]tt.snap_settings;[aria-label]tt.snap_settings" aria-haspopup="true" aria-expanded="false">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            </button>`
      : '';
    overlay.innerHTML = `
      <div class="potree-dialog" role="dialog" aria-modal="true">
        <div class="potree-dialog-header">
          <span class="potree-dialog-title"${titleI18nKey ? ` data-i18n="${titleI18nKey}"` : ''}>${titleText}</span>
          <div class="potree-dialog-header-actions">
            ${snapBtnHtml}
            <button class="potree-dialog-pin" aria-label="Pin panel" title="Pin panel" data-i18n="[title]tt.pin_panel;[aria-label]tt.pin_panel">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/></svg>
            </button>
            <button class="potree-dialog-close" aria-label="Close" title="Close" data-i18n="[title]tt.close_panel;[aria-label]tt.close_panel">&#x2715;</button>
          </div>
        </div>
        <div class="potree-dialog-body"></div>
      </div>
    `;
    document.body.appendChild(overlay);

    const body = overlay.querySelector('.potree-dialog-body');

    // Shared floating properties panel
    const propertiesFloater = document.createElement('div');
    propertiesFloater.className = 'potree-item-properties-panel';
    const propertiesEl = document.createElement('div');
    propertiesEl.className = 'potree-item-properties-body';
    propertiesFloater.appendChild(propertiesEl);
    document.body.appendChild(propertiesFloater);

    const pinBtn = overlay.querySelector('.potree-dialog-pin');
    let justOpened = false;
    let selectedItem = null;
    let snapFlyout = null;

    if (options.snapSettings) {
      snapFlyout = this._attachMeasurementSnapHeaderButton(overlay);
    }

    const closePanel = (keepPin = false) => {
      if (!keepPin) this._panelPin = false;
      overlay.classList.remove('is-open', 'is-pinned');
      propertiesFloater.classList.remove('is-open');
      pinBtn.classList.remove('active');
      selectedItem = null;
      if (snapFlyout) {
        snapFlyout.hidden = true;
        snapFlyout.classList.remove('is-open');
        const snapBtn = overlay.querySelector('.potree-dialog-snap');
        if (snapBtn) {
          snapBtn.classList.remove('active');
          snapBtn.setAttribute('aria-expanded', 'false');
        }
      }
    };

    this._registerPanel(overlay, closePanel, pinBtn);

    overlay.querySelector('.potree-dialog-close').addEventListener('click', () => closePanel());

    document.addEventListener('click', (e) => {
      if (!overlay.classList.contains('is-open')) return;
      if (justOpened) return;
      if (this._panelPin) return;
      // Ignore clicks on jQuery UI widgets appended to body (selectmenu dropdowns, color pickers, etc.)
      if (e.target.closest('.ui-selectmenu-menu, .ui-selectmenu-open, .ui-menu, .sp-container')) return;
      if (snapFlyout && snapFlyout.contains(e.target)) return;
      const dialog = overlay.querySelector('.potree-dialog');
      if (dialog.contains(e.target)) {
        if (!e.target.closest('.content-panel-color-wrap')) {
          overlay.querySelectorAll('.content-panel-palette.is-open').forEach(p => p.classList.remove('is-open'));
        }
        return;
      }
      if (propertiesFloater.contains(e.target)) return;
      closePanel();
    }, true);

    document.addEventListener('click', (e) => {
      if (!propertiesFloater.classList.contains('is-open')) return;
      if (propertiesFloater.contains(e.target)) return;
      if (overlay.contains(e.target)) return;
      // Ignore clicks on jQuery UI widgets appended to body (selectmenu dropdowns, color pickers, etc.)
      if (e.target.closest('.ui-selectmenu-menu, .ui-selectmenu-open, .ui-menu, .sp-container')) return;
      propertiesFloater.classList.remove('is-open');
      overlay.querySelectorAll('.content-panel-item.properties-open').forEach(el => el.classList.remove('properties-open'));
      overlay.querySelectorAll('.content-panel-props-btn.active').forEach(el => el.classList.remove('active'));
      selectedItem = null;
    }, true);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
        if (document.activeElement && document.activeElement.classList.contains('content-panel-name-input')) return;
        closePanel();
      }
    });

    // Build sections
    const sections = {};
    let expandedSectionId = sectionConfigs[0].id;

    for (const cfg of sectionConfigs) {
      const sectionEl = document.createElement('div');
      sectionEl.className = 'content-section';
      sectionEl.dataset.section = cfg.id;

      const header = document.createElement('div');
      header.className = 'content-section-header';
      header.innerHTML = `<span${cfg.i18nKey ? ` data-i18n="${cfg.i18nKey}"` : ''}>${cfg.title}</span><span class="content-section-chevron">&#x25B6;</span>`;
      sectionEl.appendChild(header);

      const sectionBody = document.createElement('div');
      sectionBody.className = 'content-section-body';

      let list = null;

      if (cfg.initCustomBody) {
        cfg.initCustomBody(sectionBody);
      } else {
        list = document.createElement('ul');
        list.className = 'content-panel-list';
        sectionBody.appendChild(list);

        const sectionFooter = document.createElement('div');
        sectionFooter.className = 'content-section-footer';

        const footerLeft = document.createElement('div');
        footerLeft.className = 'content-section-footer-left';

        if (cfg.footerHtml) {
          footerLeft.innerHTML = cfg.footerHtml;
          if (cfg.onFooterInit) cfg.onFooterInit(footerLeft);
        }

        if (!cfg.noVisibilityButtons) {
          const showAllBtn = document.createElement('button');
          showAllBtn.className = 'content-section-visibility-btn';
          showAllBtn.setAttribute('data-i18n', '[title]tt.show_all');
          showAllBtn.title = 'Show all';
          showAllBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
          showAllBtn.addEventListener('click', () => {
            cfg.getItems().forEach(item => cfg.setVisible(item, true));
            render();
          });

          const hideAllBtn = document.createElement('button');
          hideAllBtn.className = 'content-section-visibility-btn';
          hideAllBtn.setAttribute('data-i18n', '[title]tt.hide_all');
          hideAllBtn.title = 'Hide all';
          hideAllBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';
          hideAllBtn.addEventListener('click', () => {
            cfg.getItems().forEach(item => cfg.setVisible(item, false));
            render();
          });

          footerLeft.appendChild(showAllBtn);
          footerLeft.appendChild(hideAllBtn);
        }
        sectionFooter.appendChild(footerLeft);

        const footerRight = document.createElement('div');
        if (cfg.removeItem) {
          const deleteAllBtn = document.createElement('button');
          deleteAllBtn.className = 'content-panel-delete-all';
          deleteAllBtn.setAttribute('data-i18n', '[title]tt.delete_all');
          deleteAllBtn.title = 'Delete all';
          deleteAllBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';
          deleteAllBtn.addEventListener('click', async () => {
            const items = cfg.getItems();
            if (items.length === 0) return;
            const msg = (typeof i18n !== 'undefined') ? i18n.t('tt.delete_all_confirm', { count: items.length }) : `Delete all ${items.length} item(s)?`;
            if (!await this._confirm(msg)) return;
            [...items].forEach(item => cfg.removeItem(item));
          });
          footerRight.appendChild(deleteAllBtn);
        }
        sectionFooter.appendChild(footerRight);

        sectionBody.appendChild(sectionFooter);
      }

      sectionEl.appendChild(sectionBody);
      body.appendChild(sectionEl);

      const makeEditable = cfg.initCustomBody ? () => {} : (li, item) => {
        const existing = li.querySelector('.content-panel-name, .content-panel-name-input');
        if (!existing) return;
        const originalName = cfg.getName(item);
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'content-panel-name-input';
        input.value = originalName;
        const commit = () => {
          const newName = input.value.trim() || originalName;
          if (cfg.setName) { cfg.setName(item, newName); } else { item.name = newName; }
          const span = document.createElement('span');
          span.className = 'content-panel-name';
          span.textContent = cfg.getName(item);
          span.title = (typeof i18n !== 'undefined') ? i18n.t('tt.rename_measurement') : 'Double-click to rename';
          span.addEventListener('dblclick', (e) => { e.preventDefault(); makeEditable(li, item); });
          input.replaceWith(span);
        };
        input.addEventListener('input', () => {
          if (cfg.setName) { cfg.setName(item, input.value); } else { item.name = input.value; }
        });
        input.addEventListener('blur', commit);
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
          else if (e.key === 'Escape') { e.preventDefault(); input.value = originalName; input.blur(); input.dispatchEvent(new Event('input')); }
          e.stopPropagation();
        });
        existing.replaceWith(input);
        input.focus();
        input.select();
      };

      const infoListenerCleanups = [];
      const render = cfg.initCustomBody ? () => {} : () => {
        while (infoListenerCleanups.length) infoListenerCleanups.pop()();
        list.innerHTML = '';
        const items = cfg.getItems();
        // Update header count
        const countSpan = header.querySelector('.content-section-count');
        if (countSpan) countSpan.textContent = `(${items.length})`;
        sectionEl.style.display = items.length === 0 ? 'none' : '';
        if (items.length === 0) return;

        // --- Folder grouping support ---
        if (cfg.getFolder) {
          const folderMap = new Map(); // folder -> item[]
          const noFolder = [];
          for (const item of items) {
            const folder = cfg.getFolder(item);
            if (folder) {
              if (!folderMap.has(folder)) folderMap.set(folder, []);
              folderMap.get(folder).push(item);
            } else {
              noFolder.push(item);
            }
          }

          // Helper: build a regular item <li>
          const buildItemLi = (item, indented = false) => {
            const li = document.createElement('li');
            li.className = 'content-panel-item'
              + (item === selectedItem ? ' properties-open' : '')
              + (indented ? ' content-panel-item--indented' : '');

            if (!cfg.noVisibilityCheckbox) {
              const label = document.createElement('label');
              label.className = 'content-panel-label';
              const cb = document.createElement('input');
              cb.type = 'checkbox';
              cb.checked = cfg.getVisible(item);
              cb.title = (typeof i18n !== 'undefined') ? i18n.t('tt.toggle_visibility') : 'Show / hide';
              cb.addEventListener('change', () => cfg.setVisible(item, cb.checked));
              label.appendChild(cb);
              li.appendChild(label);
            }

            const nameSpan = document.createElement('span');
            nameSpan.className = 'content-panel-name';
            nameSpan.textContent = cfg.getName(item);
            nameSpan.title = (typeof i18n !== 'undefined') ? i18n.t('tt.rename_measurement') : 'Double-click to rename';
            nameSpan.addEventListener('dblclick', (e) => { e.preventDefault(); makeEditable(li, item); });
            li.appendChild(nameSpan);

            if (cfg.getInfo) {
              const infoSpan = document.createElement('span');
              infoSpan.className = 'content-panel-info';
              const updateInfo = () => { infoSpan.textContent = cfg.getInfo(item) || ''; };
              updateInfo();
              li.appendChild(infoSpan);
              if (typeof item.addEventListener === 'function') {
                item.addEventListener('marker_added', updateInfo);
                item.addEventListener('marker_removed', updateInfo);
                item.addEventListener('marker_moved', updateInfo);
                // Surfaces re-integrate their volume on every slider change.
                item.addEventListener('changed', updateInfo);
                infoListenerCleanups.push(() => {
                  item.removeEventListener('marker_added', updateInfo);
                  item.removeEventListener('marker_removed', updateInfo);
                  item.removeEventListener('marker_moved', updateInfo);
                  item.removeEventListener('changed', updateInfo);
                });
              }
            }

            if (item.color && typeof item.color.getHexString === 'function') {
              const PALETTE = [
                '#ffffff','#ff0000','#ff6e00','#ffd900',
                '#b7ff00','#48ff00','#00ff22','#00ff90',
                '#00ffff','#0095ff','#0026ff','#4800ff',
                '#b300ff','#ff00dd','#ff006e','#000000',
              ];
              const swatch = document.createElement('button');
              swatch.className = 'content-panel-color';
              swatch.title = (typeof i18n !== 'undefined') ? i18n.t('tt.change_color') : 'Change color';
              swatch.style.background = '#' + item.color.getHexString();
              const palette = document.createElement('div');
              palette.className = 'content-panel-palette';
              PALETTE.forEach(hex => {
                const dot = document.createElement('button');
                dot.className = 'content-panel-palette-dot';
                dot.style.background = hex;
                dot.title = hex;
                dot.addEventListener('click', (e) => {
                  e.stopPropagation();
                  item.color.set(hex);
                  if (typeof item.updateColor === 'function') item.updateColor();
                  swatch.style.background = hex;
                  palette.classList.remove('is-open');
                });
                palette.appendChild(dot);
              });
              swatch.addEventListener('click', (e) => {
                e.stopPropagation();
                const isOpen = palette.classList.toggle('is-open');
                if (isOpen) {
                  overlay.querySelectorAll('.content-panel-palette.is-open').forEach(p => {
                    if (p !== palette) p.classList.remove('is-open');
                  });
                }
              });
              const colorWrap = document.createElement('div');
              colorWrap.className = 'content-panel-color-wrap';
              colorWrap.appendChild(swatch);
              colorWrap.appendChild(palette);
              li.appendChild(colorWrap);
            }

            if (cfg.onItemClick) {
              const propsBtn = document.createElement('button');
              propsBtn.className = 'content-panel-props-btn';
              propsBtn.title = (typeof i18n !== 'undefined') ? i18n.t('tt.properties') : 'Properties';
              propsBtn.innerHTML = '&#x2139;';
              propsBtn.addEventListener('click', () => {
                const alreadyOpen = item === selectedItem;
                overlay.querySelectorAll('.content-panel-item.properties-open').forEach(el => el.classList.remove('properties-open'));
                overlay.querySelectorAll('.content-panel-props-btn.active').forEach(el => el.classList.remove('active'));
                if (alreadyOpen) {
                  selectedItem = null;
                  propertiesFloater.classList.remove('is-open');
                } else {
                  selectedItem = item;
                  li.classList.add('properties-open');
                  propsBtn.classList.add('active');
                  propertiesFloater.classList.add('is-open');
                  cfg.onItemClick(item, propertiesEl);
                }
              });
              li.appendChild(propsBtn);
            }

            if (cfg.extraButtons) {
              for (const btn of cfg.extraButtons) {
                const btnEl = document.createElement('button');
                btnEl.className = btn.className || 'content-panel-extra-btn';
                btnEl.title = btn.title || '';
                btnEl.innerHTML = btn.html || '';
                btnEl.addEventListener('click', (e) => btn.onClick(item, e));
                li.appendChild(btnEl);
              }
            }

            if (cfg.removeItem) {
              const delBtn = document.createElement('button');
              delBtn.className = 'content-panel-delete';
              delBtn.title = (typeof i18n !== 'undefined') ? i18n.t('tt.delete') : 'Delete';
              delBtn.innerHTML = '&#x2715;';
              delBtn.addEventListener('click', () => cfg.removeItem(item));
              li.appendChild(delBtn);
            }

            return li;
          };

          // Render non-grouped items first
          for (const item of noFolder) {
            list.appendChild(buildItemLi(item, false));
          }

          // Render folder groups
          for (const [folderName, folderItems] of folderMap) {
            // Track per-folder collapse state in a closure-local Map on the list element
            if (!list._folderOpen) list._folderOpen = {};
            if (list._folderOpen[folderName] === undefined) list._folderOpen[folderName] = true;

            const folderLi = document.createElement('li');
            folderLi.className = 'content-panel-folder';

            // Folder visibility checkbox (toggles all children)
            if (!cfg.noVisibilityCheckbox) {
              const label = document.createElement('label');
              label.className = 'content-panel-label';
              const cb = document.createElement('input');
              cb.type = 'checkbox';
              const allVisible = folderItems.every(i => cfg.getVisible(i));
              cb.checked = allVisible;
              cb.indeterminate = !allVisible && folderItems.some(i => cfg.getVisible(i));
              cb.title = (typeof i18n !== 'undefined') ? i18n.t('tt.toggle_visibility') : 'Show / hide all';
              cb.addEventListener('change', () => {
                folderItems.forEach(i => cfg.setVisible(i, cb.checked));
              });
              label.appendChild(cb);
              folderLi.appendChild(label);
            }

            // Chevron + folder name
            const chevron = document.createElement('span');
            chevron.className = 'content-panel-folder-chevron';
            chevron.textContent = list._folderOpen[folderName] ? '▾' : '▸';
            folderLi.appendChild(chevron);

            const nameSpan = document.createElement('span');
            nameSpan.className = 'content-panel-folder-name';
            nameSpan.textContent = folderName;
            folderLi.appendChild(nameSpan);

            list.appendChild(folderLi);

            // Child items
            const childrenWrap = document.createElement('ul');
            childrenWrap.className = 'content-panel-folder-children';
            if (!list._folderOpen[folderName]) childrenWrap.style.display = 'none';

            for (const item of folderItems) {
              childrenWrap.appendChild(buildItemLi(item, true));
            }
            list.appendChild(childrenWrap);

            // Toggle collapse on click
            const toggle = () => {
              list._folderOpen[folderName] = !list._folderOpen[folderName];
              chevron.textContent = list._folderOpen[folderName] ? '▾' : '▸';
              childrenWrap.style.display = list._folderOpen[folderName] ? '' : 'none';
            };
            folderLi.addEventListener('click', (e) => {
              if (e.target.type === 'checkbox') return;
              toggle();
            });
          }

          return; // skip the original flat render below
        }
        // --- end folder grouping ---

        for (const item of items) {
          const li = document.createElement('li');
          li.className = 'content-panel-item'
            + (item === selectedItem ? ' properties-open' : '');

          if (!cfg.noVisibilityCheckbox) {
            const label = document.createElement('label');
            label.className = 'content-panel-label';
            const cb = document.createElement('input');
            cb.type = 'checkbox';
            cb.checked = cfg.getVisible(item);
            cb.title = (typeof i18n !== 'undefined') ? i18n.t('tt.toggle_visibility') : 'Show / hide';
            cb.addEventListener('change', () => cfg.setVisible(item, cb.checked));
            label.appendChild(cb);
            li.appendChild(label);
          }

          const nameSpan = document.createElement('span');
          nameSpan.className = 'content-panel-name';
          nameSpan.textContent = cfg.getName(item);
          nameSpan.title = (typeof i18n !== 'undefined') ? i18n.t('tt.rename_measurement') : 'Double-click to rename';
          nameSpan.addEventListener('dblclick', (e) => { e.preventDefault(); makeEditable(li, item); });
          li.appendChild(nameSpan);

          if (cfg.getInfo) {
            const infoSpan = document.createElement('span');
            infoSpan.className = 'content-panel-info';
            const updateInfo = () => { infoSpan.textContent = cfg.getInfo(item) || ''; };
            updateInfo();
            li.appendChild(infoSpan);
            if (typeof item.addEventListener === 'function') {
              item.addEventListener('marker_added', updateInfo);
              item.addEventListener('marker_removed', updateInfo);
              item.addEventListener('marker_moved', updateInfo);
              item.addEventListener('changed', updateInfo);
              infoListenerCleanups.push(() => {
                item.removeEventListener('marker_added', updateInfo);
                item.removeEventListener('marker_removed', updateInfo);
                item.removeEventListener('marker_moved', updateInfo);
                item.removeEventListener('changed', updateInfo);
              });
            }
          }

          if (item.color && typeof item.color.getHexString === 'function') {
            const PALETTE = [
              '#ffffff','#ff0000','#ff6e00','#ffd900',
              '#b7ff00','#48ff00','#00ff22','#00ff90',
              '#00ffff','#0095ff','#0026ff','#4800ff',
              '#b300ff','#ff00dd','#ff006e','#000000',
            ];
            const swatch = document.createElement('button');
            swatch.className = 'content-panel-color';
            swatch.title = (typeof i18n !== 'undefined') ? i18n.t('tt.change_color') : 'Change color';
            swatch.style.background = '#' + item.color.getHexString();
            const palette = document.createElement('div');
            palette.className = 'content-panel-palette';
            PALETTE.forEach(hex => {
              const dot = document.createElement('button');
              dot.className = 'content-panel-palette-dot';
              dot.style.background = hex;
              dot.title = hex;
              dot.addEventListener('click', (e) => {
                e.stopPropagation();
                item.color.set(hex);
                if (typeof item.updateColor === 'function') item.updateColor();
                swatch.style.background = hex;
                palette.classList.remove('is-open');
              });
              palette.appendChild(dot);
            });
            swatch.addEventListener('click', (e) => {
              e.stopPropagation();
              const isOpen = palette.classList.toggle('is-open');
              if (isOpen) {
                overlay.querySelectorAll('.content-panel-palette.is-open').forEach(p => {
                  if (p !== palette) p.classList.remove('is-open');
                });
              }
            });
            const colorWrap = document.createElement('div');
            colorWrap.className = 'content-panel-color-wrap';
            colorWrap.appendChild(swatch);
            colorWrap.appendChild(palette);
            li.appendChild(colorWrap);
          }

          if (cfg.onItemClick) {
            const propsBtn = document.createElement('button');
            propsBtn.className = 'content-panel-props-btn';
            propsBtn.title = (typeof i18n !== 'undefined') ? i18n.t('tt.properties') : 'Properties';
            propsBtn.innerHTML = '&#x2139;';
            propsBtn.addEventListener('click', () => {
              const alreadyOpen = item === selectedItem;
              overlay.querySelectorAll('.content-panel-item.properties-open').forEach(el => el.classList.remove('properties-open'));
              overlay.querySelectorAll('.content-panel-props-btn.active').forEach(el => el.classList.remove('active'));
              if (alreadyOpen) {
                selectedItem = null;
                propertiesFloater.classList.remove('is-open');
              } else {
                selectedItem = item;
                li.classList.add('properties-open');
                propsBtn.classList.add('active');
                propertiesFloater.classList.add('is-open');
                cfg.onItemClick(item, propertiesEl);
              }
            });
            li.appendChild(propsBtn);
          }

          if (cfg.extraButtons) {
            for (const btn of cfg.extraButtons) {
              const el = document.createElement('button');
              el.className = btn.className || 'content-panel-props-btn';
              // Resolve title via i18n at render time (handles keys added after i18n init)
              const resolvedTitle = btn.i18nKey && typeof i18n !== 'undefined'
                ? i18n.t(btn.i18nKey, { defaultValue: btn.title || '' })
                : (btn.title || '');
              el.title = resolvedTitle;
              el.setAttribute('aria-label', resolvedTitle);
              el.innerHTML = btn.html;
              el.addEventListener('click', (e) => btn.onClick(item, e));
              li.appendChild(el);
            }
          }

          if (cfg.removeItem) {
            const delBtn = document.createElement('button');
            delBtn.className = 'content-panel-delete-btn';
            delBtn.title = (typeof i18n !== 'undefined') ? i18n.t('tt.delete_item') : 'Delete';
            delBtn.innerHTML = '&#x2715;';
            delBtn.addEventListener('click', () => cfg.removeItem(item));
            li.appendChild(delBtn);
          }

          list.appendChild(li);
        }
      };

      if (cfg.sceneEvents) {
        for (const [target, eventName] of cfg.sceneEvents) {
          target.addEventListener(eventName, render);
        }
      }

      header.addEventListener('click', () => expandSection(cfg.id));

      // Add count badge to header (skip for custom-body sections)
      if (!cfg.noCount && !cfg.initCustomBody && cfg.getItems) {
        const titleSpan = header.querySelector('span');
        const countSpan = document.createElement('span');
        countSpan.className = 'content-section-count';
        countSpan.textContent = `(${cfg.getItems().length})`;
        titleSpan.after(document.createTextNode(' '), countSpan);
      }

      sections[cfg.id] = { sectionEl, header, sectionBody, list, render, makeEditable, cfg };
    }

    const expandSection = (sectionId) => {
      expandedSectionId = sectionId;
      propertiesFloater.classList.remove('is-open');
      selectedItem = null;
      for (const [id, s] of Object.entries(sections)) {
        const isExpanded = id === sectionId;
        s.sectionEl.classList.toggle('is-expanded', isExpanded);
        if (isExpanded) s.render();
      }
    };

    const openCombined = (sectionId, item = null, opts = {}) => {
      this._beforeOpenPanel(overlay);
      for (const s of Object.values(sections)) s.render();
      expandSection(sectionId);
      $(overlay).i18n();
      overlay.classList.add('is-open');
      justOpened = true;
      setTimeout(() => { justOpened = false; }, 0);
      if (item) {
        const s = sections[sectionId];
        const items = s.cfg.getItems();
        const idx = items.indexOf(item);
        const el = s.list.querySelectorAll('.content-panel-item')[idx];
        if (el) {
          el.scrollIntoView({ block: 'nearest' });
          if (opts.rename === false) {
            overlay.querySelectorAll('.content-panel-item.is-highlighted').forEach(e => e.classList.remove('is-highlighted'));
            el.classList.add('is-highlighted');
            setTimeout(() => el.classList.remove('is-highlighted'), 1500);
          } else {
            s.makeEditable(el, item);
          }
        }
      }
    };

    expandSection(sectionConfigs[0].id);

    const result = {};
    for (const cfg of sectionConfigs) {
      if (cfg.actionKey) {
        result[cfg.actionKey] = (item, opts) => openCombined(cfg.id, item, opts);
      }
    }
    result._sections = sections;
    return result;
  }

  initContentPanels() {
    const scene = this.viewer.scene;

    const createPanel = (id, title, getItems, getName, getVisible, setVisible, sceneEvents, removeItem = null, footerLeft = null, onFooterInit = null, onItemClick = null, i18nKey = null) => {
      const overlay = document.createElement('div');
      overlay.id = id + '-overlay';
      overlay.className = 'potree-dialog-overlay no-backdrop';
      const hasFooter = removeItem || footerLeft;
      overlay.innerHTML = `
        <div class="potree-dialog" role="dialog" aria-modal="true">
          <div class="potree-dialog-header">
            <span class="potree-dialog-title"${i18nKey ? ` data-i18n="${i18nKey}"` : ''}>${title}</span>
            <div class="potree-dialog-header-actions">
              <button class="potree-dialog-pin" aria-label="Pin panel" title="Pin panel" data-i18n="[title]tt.pin_panel;[aria-label]tt.pin_panel">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/></svg>
              </button>
              <button class="potree-dialog-close" aria-label="Close" title="Close" data-i18n="[title]tt.close_panel;[aria-label]tt.close_panel">&#x2715;</button>
            </div>
          </div>
          <div class="potree-dialog-body">
            <ul class="content-panel-list"></ul>
          </div>
          ${hasFooter ? `<div class="potree-dialog-footer"><div class="potree-dialog-footer-left">${footerLeft || ''}</div>${removeItem ? '<button class="content-panel-delete-all" data-i18n="[title]tt.delete_all" title="Delete all"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>' : ''}</div>` : ''}
        </div>
      `;
      document.body.appendChild(overlay);

      const list = overlay.querySelector('.content-panel-list');

      // Optional floating properties panel (rendered to the left of this panel)
      let propertiesFloater = null;
      let propertiesEl = null;
      if (onItemClick) {
        propertiesFloater = document.createElement('div');
        propertiesFloater.className = 'potree-item-properties-panel';

        propertiesEl = document.createElement('div');
        propertiesEl.className = 'potree-item-properties-body';
        propertiesFloater.appendChild(propertiesEl);
        document.body.appendChild(propertiesFloater);
      }

      const pinBtn = overlay.querySelector('.potree-dialog-pin');

      const closePanel = (keepPin = false) => {
        if (!keepPin) this._panelPin = false;
        overlay.classList.remove('is-open', 'is-pinned');
        if (propertiesFloater) propertiesFloater.classList.remove('is-open');
        pinBtn.classList.remove('active');
      };

      this._registerPanel(overlay, closePanel, pinBtn);

      let justOpened = false;

      overlay.querySelector('.potree-dialog-close').addEventListener('click', () => closePanel());

      document.addEventListener('click', (e) => {
        if (!overlay.classList.contains('is-open')) return;
        if (justOpened) return;
        if (this._panelPin) return;
        const dialog = overlay.querySelector('.potree-dialog');
        // Click inside the main panel
        if (dialog.contains(e.target)) {
          if (!e.target.closest('.content-panel-color-wrap')) {
            overlay.querySelectorAll('.content-panel-palette.is-open').forEach(p => p.classList.remove('is-open'));
          }
          return;
        }
        // Click inside the floating properties sub-panel
        if (propertiesFloater && propertiesFloater.contains(e.target)) return;
        // Click was outside both panels — close them
        closePanel();
      }, true);

      let selectedItem = null;

      const makeEditable = (li, item) => {
        const existing = li.querySelector('.content-panel-name, .content-panel-name-input');
        if (!existing) return;
        const usesTitle = (item && ('title' in item));
        const originalName = (usesTitle ? item.title : item.name) || '';
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'content-panel-name-input';
        input.value = originalName;
        const commit = () => {
          const nextName = input.value.trim() || originalName;
          if (usesTitle) {
            item.title = nextName;
          } else {
            item.name = nextName;
          }
          const span = document.createElement('span');
          span.className = 'content-panel-name';
          span.textContent = getName(item);
          span.title = (typeof i18n !== 'undefined') ? i18n.t('tt.rename_measurement') : 'Double-click to rename';
          span.addEventListener('dblclick', (e) => { e.preventDefault(); makeEditable(li, item); });
          input.replaceWith(span);
        };
        input.addEventListener('input', () => {
          if (usesTitle) { item.title = input.value; }
          else { item.name = input.value; }
        });
        input.addEventListener('blur', commit);
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
          else if (e.key === 'Escape') { e.preventDefault(); input.value = originalName; input.blur(); input.dispatchEvent(new Event('input')); }
          e.stopPropagation();
        });
        existing.replaceWith(input);
        input.focus();
        input.select();
      };

      const render = () => {
        list.innerHTML = '';
        const items = getItems();
        if (items.length === 0) {
          const li = document.createElement('li');
          li.className = 'content-panel-empty';
          li.textContent = 'No items';
          li.setAttribute('data-i18n', 'tt.no_items');
          list.appendChild(li);
          return;
        }
        for (const item of items) {
          const li = document.createElement('li');
          li.className = 'content-panel-item'
            + (item === selectedItem ? ' properties-open' : '');

          const label = document.createElement('label');
          label.className = 'content-panel-label';

          const cb = document.createElement('input');
          cb.type = 'checkbox';
          cb.checked = getVisible(item);
          cb.title = (typeof i18n !== 'undefined') ? i18n.t('tt.toggle_visibility') : 'Show / hide';
          cb.addEventListener('change', () => setVisible(item, cb.checked));

          label.appendChild(cb);
          li.appendChild(label);

          const nameSpan = document.createElement('span');
          nameSpan.className = 'content-panel-name';
          nameSpan.textContent = getName(item);
          nameSpan.title = (typeof i18n !== 'undefined') ? i18n.t('tt.rename_measurement') : 'Double-click to rename';
          nameSpan.addEventListener('dblclick', (e) => { e.preventDefault(); makeEditable(li, item); });
          li.appendChild(nameSpan);

          if (item.color && typeof item.color.getHexString === 'function') {
            const PALETTE = [
              '#ffffff','#ff0000','#ff6e00','#ffd900',
              '#b7ff00','#48ff00','#00ff22','#00ff90',
              '#00ffff','#0095ff','#0026ff','#4800ff',
              '#b300ff','#ff00dd','#ff006e','#000000',
            ];

            const swatch = document.createElement('button');
            swatch.className = 'content-panel-color';
            swatch.title = (typeof i18n !== 'undefined') ? i18n.t('tt.change_color') : 'Change color';
            swatch.style.background = '#' + item.color.getHexString();

            const palette = document.createElement('div');
            palette.className = 'content-panel-palette';

            PALETTE.forEach(hex => {
              const dot = document.createElement('button');
              dot.className = 'content-panel-palette-dot';
              dot.style.background = hex;
              dot.title = hex;
              dot.addEventListener('click', (e) => {
                e.stopPropagation();
                item.color.set(hex);
                if (typeof item.updateColor === 'function') item.updateColor();
                swatch.style.background = hex;
                palette.classList.remove('is-open');
              });
              palette.appendChild(dot);
            });

            swatch.addEventListener('click', (e) => {
              e.stopPropagation();
              const isOpen = palette.classList.toggle('is-open');
              if (isOpen) {
                // close other open palettes
                overlay.querySelectorAll('.content-panel-palette.is-open').forEach(p => {
                  if (p !== palette) p.classList.remove('is-open');
                });
              }
            });

            const colorWrap = document.createElement('div');
            colorWrap.className = 'content-panel-color-wrap';
            colorWrap.appendChild(swatch);
            colorWrap.appendChild(palette);
            li.appendChild(colorWrap);
          }

          if (onItemClick) {
            const propsBtn = document.createElement('button');
            propsBtn.className = 'content-panel-props-btn';
            propsBtn.title = (typeof i18n !== 'undefined') ? i18n.t('tt.properties') : 'Properties';
            propsBtn.innerHTML = '&#x2139;';
            propsBtn.addEventListener('click', () => {
              const alreadyOpen = item === selectedItem;
              list.querySelectorAll('.content-panel-item.properties-open').forEach(el => el.classList.remove('properties-open'));
              list.querySelectorAll('.content-panel-props-btn.active').forEach(el => el.classList.remove('active'));
              if (alreadyOpen) {
                selectedItem = null;
                propertiesFloater.classList.remove('is-open');
              } else {
                selectedItem = item;
                li.classList.add('properties-open');
                propsBtn.classList.add('active');
                propertiesFloater.classList.add('is-open');
                onItemClick(item, propertiesEl);
              }
            });
            li.appendChild(propsBtn);
          }

          if (removeItem) {
            const delBtn = document.createElement('button');
            delBtn.className = 'content-panel-delete-btn';
            delBtn.title = (typeof i18n !== 'undefined') ? i18n.t('tt.delete_item') : 'Delete';
            delBtn.innerHTML = '&#x2715;';
            delBtn.addEventListener('click', () => removeItem(item));
            li.appendChild(delBtn);
          }

          list.appendChild(li);
        }
      };

      if (removeItem) {
        const deleteAllBtn = overlay.querySelector('.content-panel-delete-all');
        deleteAllBtn.addEventListener('click', async () => {
          const items = getItems();
          if (items.length === 0) return;
          const msg = (typeof i18n !== 'undefined') ? i18n.t('tt.delete_all_confirm', { count: items.length }) : `Delete all ${items.length} item(s)?`;
          if (!await this._confirm(msg)) return;
          [...items].forEach(item => removeItem(item));
        });
      }

      if (onFooterInit) onFooterInit(overlay);

      if (propertiesFloater) {
        document.addEventListener('click', (e) => {
          if (!propertiesFloater.classList.contains('is-open')) return;
          if (propertiesFloater.contains(e.target)) return;
          if (overlay.contains(e.target)) return;
          // click was outside both panels — close properties
          propertiesFloater.classList.remove('is-open');
          list.querySelectorAll('.content-panel-item.properties-open').forEach(el => el.classList.remove('properties-open'));
          list.querySelectorAll('.content-panel-props-btn.active').forEach(el => el.classList.remove('active'));
          selectedItem = null;
        }, true);
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
          if (document.activeElement && document.activeElement.classList.contains('content-panel-name-input')) return;
          closePanel();
        }
      });

      for (const [target, eventName] of sceneEvents) {
        target.addEventListener(eventName, render);
      }

      return (item = null, opts = {}) => {
        this._beforeOpenPanel(overlay);
        render();
        $(overlay).i18n();
        overlay.classList.add('is-open');
        justOpened = true;
        setTimeout(() => { justOpened = false; }, 0);
        if (item) {
          const items = getItems();
          const idx = items.indexOf(item);
          const el = list.querySelectorAll('.content-panel-item')[idx];
          if (el) {
            el.scrollIntoView({ block: 'nearest' });
            if (opts.rename === false) {
              list.querySelectorAll('.content-panel-item.is-highlighted').forEach(e => e.classList.remove('is-highlighted'));
              el.classList.add('is-highlighted');
              setTimeout(() => el.classList.remove('is-highlighted'), 1500);
            } else {
              makeEditable(el, item);
            }
            // Auto-open properties panel if available (not on rename/creation)
            if (opts.rename === false && onItemClick && propertiesFloater) {
              list.querySelectorAll('.content-panel-item.properties-open').forEach(e => e.classList.remove('properties-open'));
              list.querySelectorAll('.content-panel-props-btn.active').forEach(e => e.classList.remove('active'));
              selectedItem = item;
              el.classList.add('properties-open');
              const propsBtn = el.querySelector('.content-panel-props-btn');
              if (propsBtn) propsBtn.classList.add('active');
              propertiesFloater.classList.add('is-open');
              onItemClick(item, propertiesEl);
            }
          }
        }
      };
    };

    // Flatten all leaf annotations recursively
    const getAnnotations = (node = scene.annotations) => {
      const result = [];
      for (const child of node.children) {
        if (child.children && child.children.length > 0) {
          result.push(...getAnnotations(child));
        } else {
          result.push(child);
        }
      }
      return result;
    };

    // ── Scene (Measurements) accordion panel ──
    const scenePanel = this._buildAccordionPanel('content-combined-overlay', 'Measurements', 'menu.scene', [
      {
        id: 'measurements',
        actionKey: 'open-measurements-panel',
        title: 'Measurements',
        i18nKey: 'menu.scene_measurements',
        getItems: () => [...scene.measurements, ...scene.profiles, ...scene.volumes.filter(v => !v.clip)],
        getName: (item) => item.name || '(unnamed)',
        getInfo: (item) => {
          if (!(item instanceof Measure) || item.points.length < 2) return '';
          if (item.showArea) return `${item.getArea().toFixed(2)} m²`;
          if (item.showDistances && !item.showAngles && !item.showHeight) {
            return `${item.getTotalDistance().toFixed(2)} m`;
          }
          return '';
        },
        getVisible: (item) => item.visible,
        setVisible: (item, v) => { item.visible = v; },
        sceneEvents: [
          [scene, 'measurement_added'], [scene, 'measurement_removed'],
          [scene, 'profile_added'],     [scene, 'profile_removed'],
          [scene, 'volume_added'],      [scene, 'volume_removed'],
        ],
        removeItem: (item) => {
          if (scene.measurements.includes(item)) scene.removeMeasurement(item);
          else if (scene.profiles.includes(item)) scene.removeProfile(item);
          else if (scene.volumes.includes(item) && !item.clip) scene.removeVolume(item);
        },
        onItemClick: (item, propsEl) => {
          if (!this._measurePropertiesPanel) {
            this._measurePropertiesPanel = new PropertiesPanel($(propsEl), this.viewer);
            this._measurePropertiesPanel.setScene(this.viewer.scene);
          } else {
            this._measurePropertiesPanel.container = $(propsEl);
            this._measurePropertiesPanel.object = null;
          }
          this._measurePropertiesPanel.set(item);
        },
        footerHtml: `<label class="content-panel-labels-toggle">
          <input type="checkbox" class="content-section-show-labels">
          <span data-i18n="tt.show_labels">Show labels</span>
        </label>
        <div class="content-panel-export-wrapper">
          <button type="button" class="content-panel-export-btn" data-i18n="[title]menu.export" title="Export" aria-haspopup="true" aria-expanded="false">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          </button>
          <div class="content-panel-export-menu" hidden role="menu">
            <button type="button" role="menuitem" data-format="dxf">DXF</button>
            <button type="button" role="menuitem" data-format="xlsx">XLSX</button>
          </div>
        </div>`,
        onFooterInit: (sectionEl) => {
          const cb = sectionEl.querySelector('.content-section-show-labels');
          cb.checked = this.measuringTool.showLabels;
          cb.addEventListener('change', () => { this.measuringTool.showLabels = cb.checked; });

          const exportBtn = sectionEl.querySelector('.content-panel-export-btn');
          const exportMenu = sectionEl.querySelector('.content-panel-export-menu');
          if (exportBtn && exportMenu) {
            const closeMenu = () => {
              exportMenu.hidden = true;
              exportBtn.setAttribute('aria-expanded', 'false');
              document.removeEventListener('mousedown', onDocClick, true);
            };
            const onDocClick = (e) => {
              if (!exportMenu.contains(e.target) && e.target !== exportBtn && !exportBtn.contains(e.target)) closeMenu();
            };
            exportBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              const open = exportMenu.hidden;
              if (open) {
                exportMenu.hidden = false;
                exportBtn.setAttribute('aria-expanded', 'true');
                document.addEventListener('mousedown', onDocClick, true);
              } else {
                closeMenu();
              }
            });
            exportMenu.querySelectorAll('button[data-format]').forEach(b => {
              b.addEventListener('click', () => {
                const format = b.dataset.format;
                closeMenu();
                const items = [...scene.measurements, ...scene.profiles, ...scene.volumes.filter(v => !v.clip)];
                this._exportMeasurements(items, format);
              });
            });
          }
        },
      },
      {
        id: 'surfaces',
        actionKey: 'open-measurements-panel',
        title: 'Surfaces',
        i18nKey: 'menu.scene_surfaces',
        getItems: () => [...(scene.surfaces || [])],
        getName: (item) => item.name || '(unnamed)',
        getInfo: (item) => {
          if (!Number.isFinite(item.areaM2)) return '';
          // Volume is null until the profile integral lands after a grow.
          const vol = plasterVolumeM3(item);
          const volStr = Number.isFinite(vol) ? ` · ${vol.toFixed(volumeDecimals(vol))} m³` : '';
          return `${item.areaM2.toFixed(2)} m²${volStr}`;
        },
        getVisible: (item) => item.visible,
        setVisible: (item, v) => { item.setVisible(v); },
        sceneEvents: [
          [scene, 'surface_added'], [scene, 'surface_removed'],
        ],
        removeItem: (item) => scene.removeSurface(item),
        onItemClick: (item, propsEl) => {
          scene.selectedSurface = item;
          if (!this._surfacePropertiesPanel) {
            this._surfacePropertiesPanel = new PropertiesPanel($(propsEl), this.viewer);
            this._surfacePropertiesPanel.setScene(this.viewer.scene);
          } else {
            this._surfacePropertiesPanel.container = $(propsEl);
            this._surfacePropertiesPanel.object = null;
          }
          this._surfacePropertiesPanel.set(item);
        },
        setName: (item, v) => { item.name = v; },
      },
      {
        id: 'annotations',
        actionKey: 'open-annotations-panel',
        title: 'Annotations',
        i18nKey: 'menu.scene_annotations',
        getItems: () => getAnnotations().filter(a => !a.cameraPosition),
        getName: (item) => item.title || '(unnamed)',
        getVisible: (item) => item.visible,
        setVisible: (item, v) => { item.visible = v; },
        sceneEvents: [
          [scene.annotations, 'annotation_added'],
          [scene.annotations, 'annotation_removed'],
          [scene.annotations, 'annotation_changed'],
        ],
        removeItem: (item) => { scene.removeAnnotation(item); },
        onItemClick: (item, propsEl) => {
          if (!this._annotationPropertiesPanel) {
            this._annotationPropertiesPanel = new PropertiesPanel($(propsEl), this.viewer);
            this._annotationPropertiesPanel.setScene(this.viewer.scene);
          } else {
            this._annotationPropertiesPanel.container = $(propsEl);
            this._annotationPropertiesPanel.object = null;
          }
          this._annotationPropertiesPanel.set(item);
        },
        setName: (item, v) => { item.title = v; },
      },
      {
        id: 'camera-annotations',
        actionKey: 'open-camera-annotations-panel',
        title: 'Camera Positions',
        noVisibilityButtons: true,
        noVisibilityCheckbox: true,
        i18nKey: 'menu.scene_camera_annotations',
        getItems: () => getAnnotations().filter(a => a.cameraPosition != null),
        getName: (item) => item.title || '(unnamed)',
        getVisible: (item) => item.visible,
        setVisible: (item, v) => { item.visible = v; },
        sceneEvents: [
          [scene.annotations, 'annotation_added'],
          [scene.annotations, 'annotation_removed'],
          [scene.annotations, 'annotation_changed'],
        ],
        removeItem: (item) => { scene.removeAnnotation(item); },
        onItemClick: null,
        setName: (item, v) => { item.title = v; },
        extraButtons: [
          {
            i18nKey: 'tt.fly_to_camera',
            title: 'Fly to camera position',
            html: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5,3 19,12 5,21"/></svg>',
            onClick: (item) => { item.moveHere(scene.getActiveCamera()); },
          },
        ],
      },
      {
        id: 'camera-animations',
        actionKey: 'open-camera-animations-panel',
        title: 'Camera Animations',
        i18nKey: 'menu.scene_camera_animations',
        getItems: () => scene.cameraAnimations,
        getName: (item) => item.name || 'Camera Animation',
        getVisible: (item) => item.visible,
        setVisible: (item, v) => { item.setVisible(v); },
        sceneEvents: [
          [scene, 'camera_animation_added'],
          [scene, 'camera_animation_removed'],
        ],
        removeItem: (item) => { scene.removeCameraAnimation(item); },
        onItemClick: (item, propsEl) => {
          if (!this._cameraAnimationPropertiesPanel) {
            this._cameraAnimationPropertiesPanel = new PropertiesPanel($(propsEl), this.viewer);
            this._cameraAnimationPropertiesPanel.setScene(this.viewer.scene);
          } else {
            this._cameraAnimationPropertiesPanel.container = $(propsEl);
            this._cameraAnimationPropertiesPanel.object = null;
          }
          this._cameraAnimationPropertiesPanel.set(item);
        },
        extraButtons: [
          {
            i18nKey: 'tt.play_animation',
            title: 'Play animation',
            html: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5,3 19,12 5,21"/></svg>',
            onClick: (item) => { item.play(); },
          },
        ],
      },
      {
        id: 'clipping',
        actionKey: 'open-clipping-panel',
        title: 'Clipping',
        i18nKey: 'menu.scene_clipping',
        noVisibilityButtons: true,
        getItems: () => scene.volumes.filter(v => v.clip),
        getName: (item) => item.name || 'Clip volume',
        getVisible: (item) => item.visible,
        setVisible: (item, v) => { item.visible = v; },
        sceneEvents: [
          [scene, 'volume_added'],
          [scene, 'volume_removed'],
        ],
        removeItem: (item) => scene.removeVolume(item),
        onItemClick: null,
        footerHtml: `<label class="clipping-panel-label" data-i18n="appearance.clip_mode">Mode</label>
          <select class="potree-select clipping-mode-select">
            <option value="NONE" data-i18n="tb.clipping_none">None</option>
            <option value="HIGHLIGHT" data-i18n="tb.clipping_highlight">Highlight</option>
            <option value="SHOW_INSIDE" data-i18n="tb.clipping_show_inside">Inside</option>
            <option value="SHOW_OUTSIDE" data-i18n="tb.clipping_show_outside">Outside</option>
          </select>`,
        onFooterInit: (footerEl) => {
          const viewer = this.viewer;
          const modeSelect = footerEl.querySelector('.clipping-mode-select');
          const syncMode = () => {
            const current = Object.keys(ClipTask).find(k => ClipTask[k] === viewer.clipTask);
            if (current) modeSelect.value = current;
          };
          syncMode();
          modeSelect.addEventListener('change', () => viewer.setClipTask(ClipTask[modeSelect.value]));
          viewer.addEventListener('cliptask_changed', syncMode);
        },
      },
    ], { snapSettings: true });

    // ── Data (Point Clouds + Panoramas) accordion panel ──
    // Cloud → linked panoramas visibility sync (issue #706). Panorama toggle
    // never touches clouds. Mesh stays independent.
    const setLinkedPanoramasVisible = (cloudUuid, v) => {
      if (!cloudUuid) return;
      for (const img of scene.images360) {
        if (img.cloudUuid === cloudUuid) {
          img.node.visible = v;
          if (v && typeof img.refreshLayerMarkerVisibility === 'function') {
            img.refreshLayerMarkerVisibility();
          }
        }
      }
    };

    const dataPanel = this._buildAccordionPanel('content-data-overlay', 'Content', 'menu.scene_data', [
      {
        id: 'point-clouds',
        actionKey: 'open-pointclouds-panel',
        title: 'Point Clouds',
        i18nKey: 'menu.scene_pointclouds',
        getItems: () => scene.pointclouds,
        getName: (item) => item.name || '(unnamed)',
        getFolder: (item) => item.folder || null,
        getVisible: (item) => item.visible,
        setVisible: (item, v) => {
          item.visible = v;
          if (item.noachCloudUuid) {
            setLinkedPanoramasVisible(item.noachCloudUuid, v);
            dataPanel._sections?.panoramas?.render?.();
          }
        },
        sceneEvents: [
          [scene, 'pointcloud_added'],
          [scene, 'pointcloud_removed'],
        ],
        removeItem: null,
        onItemClick: (item, propsEl) => {
          if (!this._pointCloudPropertiesPanel) {
            this._pointCloudPropertiesPanel = new PropertiesPanel($(propsEl), this.viewer);
            this._pointCloudPropertiesPanel.setScene(this.viewer.scene);
          } else {
            this._pointCloudPropertiesPanel.container = $(propsEl);
            this._pointCloudPropertiesPanel.object = null;
          }
          this._pointCloudPropertiesPanel.set(item);
        },
      },
      {
        id: 'panoramas',
        actionKey: 'open-images-panel',
        title: 'Panoramas',
        i18nKey: 'menu.scene_panoramas',
        getItems: () => [...scene.orientedImages, ...scene.images360],
        getName: (item) => item.displayName || item.name || 'images',
        getVisible: (item) =>
          item instanceof Images360 ? item.node.visible : item.visible,
        setVisible: (item, v) => {
          if (item instanceof Images360) {
            item.node.visible = v;
            if (v) {
              item.refreshLayerMarkerVisibility();
            }
          } else {
            item.visible = v;
          }
        },
        sceneEvents: [
          [scene, 'oriented_images_added'],
          [scene, '360_images_added'],
        ],
        removeItem: null,
        onItemClick: null,
      },
      {
        id: 'reality-meshes',
        actionKey: 'open-reality-meshes-panel',
        title: 'Meshes',
        i18nKey: 'menu.scene_reality_meshes',
        // BIM models also call addRealityMesh for clipping, but they get their
        // own Content section — keep this list to real 3D Tiles meshes only.
        getItems: () =>
          (scene.realityMeshes || []).filter((m) => !m?.userData?.isBimModel),
        getName: (item) => item.name || 'Reality Mesh',
        // Reality meshes use Cesium-style `show` (boolean) when they are
        // Cesium3DTileset instances, and `visible` when they are three.js groups.
        getVisible: (item) => {
          if (typeof item.show === 'boolean') return item.show;
          if (typeof item.visible === 'boolean') return item.visible;
          return true;
        },
        setVisible: (item, v) => {
          if ('show' in item) item.show = v;
          if ('visible' in item) item.visible = v;
        },
        sceneEvents: [
          [scene, 'reality_mesh_added'],
          [scene, 'reality_mesh_removed'],
        ],
        removeItem: null,
        onItemClick: null,
        // Per-item gear icon → dispatch a window event that noach-fe's
        // PotreeViewer listens for. We do not own the mesh state here
        // (it lives in React); we just signal "user wants to edit this
        // mesh's offset" and the host UI takes over.
        extraButtons: [
          {
            className: 'content-panel-extra-btn content-panel-mesh-offset-btn',
            title: 'Adjust position',
            html: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
            // Toggle an inline offset panel directly underneath the mesh's
            // <li> in the Content sidebar. The actual UI is rendered by
            // noach-fe (PotreeViewer.tsx) via React Portal into the host
            // <div> we attach here — that way the submodule stays styling-
            // and i18n-agnostic while the host app keeps its design system.
            //
            // The host element is recreated each time the panel is opened
            // (and removed on close) so React's portal lifetime is bounded
            // by a single edit session; if the sidebar list is rebuilt
            // (e.g. mesh added/removed event) the host element is detached
            // from the DOM and a MutationObserver on the React side closes
            // the panel.
            onClick: (item, e) => {
              if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
              const meshId = item && item.noachMeshId;
              if (!meshId) {
                console.warn('[REALITY_MESH] gear icon: item has no noachMeshId; ignoring');
                return;
              }

              const button = e && e.currentTarget;
              const itemLi = button && typeof button.closest === 'function'
                ? button.closest('.content-panel-item')
                : null;
              if (!itemLi) {
                console.warn('[REALITY_MESH] gear icon: cannot locate parent <li>');
                return;
              }

              // Find an existing offset row for this mesh (might be the
              // immediate sibling after `itemLi`).
              let row = itemLi.nextElementSibling;
              const matchesRow = row
                && row.classList
                && row.classList.contains('content-panel-mesh-offset-row')
                && row.dataset
                && row.dataset.meshId === meshId;

              if (matchesRow) {
                // Already open → close: remove the row entirely and tell
                // React to tear down its portal (so it can also revert
                // any unsaved live-preview offset).
                row.remove();
                itemLi.classList.remove('content-panel-item--offset-open');
                if (button.classList) button.classList.remove('active');
                window.dispatchEvent(
                  new CustomEvent('reality-mesh-edit-offset', {
                    detail: { meshId, expanded: false, hostElement: null },
                  }),
                );
                return;
              }

              // Different mesh has a row open in the same list? Close it
              // first so we never have two panels expanded simultaneously
              // (sidebar is narrow — one at a time keeps it usable).
              const list = itemLi.parentElement;
              if (list) {
                const stale = list.querySelector(':scope > .content-panel-mesh-offset-row');
                if (stale) {
                  const staleId = stale.dataset && stale.dataset.meshId;
                  stale.remove();
                  if (staleId) {
                    window.dispatchEvent(
                      new CustomEvent('reality-mesh-edit-offset', {
                        detail: { meshId: staleId, expanded: false, hostElement: null },
                      }),
                    );
                  }
                }
                list.querySelectorAll('.content-panel-item--offset-open').forEach(el => {
                  el.classList.remove('content-panel-item--offset-open');
                });
                list.querySelectorAll('.content-panel-mesh-offset-btn.active').forEach(el => {
                  el.classList.remove('active');
                });
              }

              row = document.createElement('li');
              row.className = 'content-panel-mesh-offset-row';
              row.dataset.meshId = meshId;
              const host = document.createElement('div');
              host.className = 'content-panel-mesh-offset-host';
              row.appendChild(host);
              itemLi.after(row);
              itemLi.classList.add('content-panel-item--offset-open');
              if (button.classList) button.classList.add('active');

              window.dispatchEvent(
                new CustomEvent('reality-mesh-edit-offset', {
                  detail: { meshId, expanded: true, hostElement: host },
                }),
              );
            },
          },
        ],
      },
      {
        // "Rzuty" (floor plans / room plans). The body is owned by noach-fe:
        // we attach an empty host <div> and signal the host app via a window
        // event so it can portal its React panel in (same decoupling as the
        // reality-mesh offset gear). The submodule stays state-/i18n-agnostic.
        id: 'plans',
        title: 'Plans',
        i18nKey: 'menu.scene_plans',
        noVisibilityButtons: true,
        initCustomBody: (sectionBody) => {
          const host = document.createElement('div');
          host.className = 'content-panel-plans-host';
          sectionBody.appendChild(host);
          // Stash for late subscribers (React may mount after init) + notify.
          window.__noachPlansHost = host;
          window.dispatchEvent(
            new CustomEvent('noach-plans-host-ready', { detail: { hostElement: host } }),
          );
        },
      },
      {
        // BIM explorer (spatial tree / Psets / category filters). Body owned by
        // noach-fe via React portal — same host pattern as Plans.
        id: 'bim',
        title: 'BIM',
        i18nKey: 'menu.scene_bim',
        noVisibilityButtons: true,
        initCustomBody: (sectionBody) => {
          const host = document.createElement('div');
          host.className = 'content-panel-bim-host';
          sectionBody.appendChild(host);
          window.__noachBimHost = host;
          window.dispatchEvent(
            new CustomEvent('noach-bim-host-ready', { detail: { hostElement: host } }),
          );
        },
      },
    ]);

    this._contentPanels = {
      ...scenePanel,
      ...dataPanel,
      'open-other-panel': createPanel(
        'content-other', 'Other',
        () => scene.cameraAnimations,
        (item) => item.name || 'animation',
        (item) => item.visible,
        (item, v) => { item.visible = v; },
        [
          [scene, 'camera_animation_added'],
          [scene, 'camera_animation_removed'],
        ],
        null, null, null, null, 'menu.scene_other'
      ),
      'open-vectors-panel': createPanel(
        'content-vectors', 'Vectors',
        () => scene.geopackages.flatMap(g => (g.node ? g.node.children : [])),
        (item) => item.name || '(unnamed)',
        (item) => item.visible,
        (item, v) => { item.visible = v; },
        [[scene, 'geopackage_added']],
        null, null, null, null, 'menu.scene_vectors'
      ),
      'open-panorama-edit-panel': this._initPanoramaEditPanel(scene),
    };

    // Wire sphere clicks → open measurements panel and highlight the item
    const openMeasurementsPanel = this._contentPanels['open-measurements-panel'];
    const attachSphereClicks = (item) => {
      if (!item.spheres) return;
      const clickHandler = () => {
        // Ignore if the sphere was dragged (moved) rather than just clicked
        if (this.viewer.inputHandler.getNormalizedDrag().length() > 0.003) return;
        openMeasurementsPanel(item, { rename: false });
      };
      // Wire any spheres already on the item (first sphere exists at measurement_added time)
      for (const sphere of item.spheres) {
        sphere.addEventListener('click', clickHandler);
      }
      // Wire spheres added after the item was created (user keeps clicking to add points)
      item.addEventListener('marker_added', (e) => {
        e.sphere.addEventListener('click', clickHandler);
      });
    };
    const onMeasurementOrProfileAdded = (e) => attachSphereClicks(e.measurement || e.profile);
    scene.addEventListener('measurement_added', onMeasurementOrProfileAdded);
    scene.addEventListener('profile_added', onMeasurementOrProfileAdded);
    for (const item of [...scene.measurements, ...scene.profiles]) {
      attachSphereClicks(item);
    }

    // Volumes use the 'select' event (no drag listeners / spheres)
    const openClippingPanel = this._contentPanels['open-clipping-panel'];
    const attachVolumeSelect = (item) => {
      item.addEventListener('select', () => {
        // Floor-plan clips are managed from the Content tab ("Rzuty"); selecting
        // one (e.g. to drag/scale it) must not yank the user to another panel.
        if (item.noachFloorClip) return;
        if (item.clip) openClippingPanel(item, { rename: false });
        else openMeasurementsPanel(item, { rename: false });
      });
    };
    scene.addEventListener('volume_added', (e) => {
      attachVolumeSelect(e.volume);
      if (!e.volume.clip) {
        openMeasurementsPanel(e.volume, { rename: false });
      }
    });
    for (const item of scene.volumes) {
      attachVolumeSelect(item);
    }

    // Annotation label click → open annotations panel with properties
    const openAnnotationsPanel = this._contentPanels['open-annotations-panel'];
    const openCameraAnnotationsPanel = this._contentPanels['open-camera-annotations-panel'];
    const attachAnnotationLabelClick = (annotation) => {
      annotation.addEventListener('annotation_label_clicked', (e) => {
        if (e.annotation.cameraPosition) {
          openCameraAnnotationsPanel(e.annotation, { rename: false });
        } else {
          openAnnotationsPanel(e.annotation, { rename: false });
        }
      });
    };
    scene.annotations.addEventListener('annotation_added', (e) => {
      attachAnnotationLabelClick(e.annotation);
    });
    for (const annotation of getAnnotations()) {
      attachAnnotationLabelClick(annotation);
    }

    // Camera animation sphere click → open panel and highlight animation
    const openCameraAnimationsPanel = this._contentPanels['open-camera-animations-panel'];
    const attachAnimationSphereClicks = (animation) => {
      animation.addEventListener('sphere_clicked', () => {
        openCameraAnimationsPanel(animation, { rename: false });
      });
    };
    scene.addEventListener('camera_animation_added', (e) => {
      attachAnimationSphereClicks(e.animation);
    });
    for (const animation of scene.cameraAnimations) {
      attachAnimationSphereClicks(animation);
    }
  }

  _initPanoramaEditPanel(scene) {
    const viewer = this.viewer;

    const overlay = document.createElement('div');
    overlay.id = 'panorama-edit-overlay';
    overlay.className = 'potree-dialog-overlay no-backdrop';
    overlay.innerHTML = `
      <div class="potree-dialog" role="dialog" aria-modal="true" style="display:flex;flex-direction:column;">
        <div class="potree-dialog-header" style="flex-shrink:0;">
          <span class="potree-dialog-title" data-i18n="menu.panorama_edit">Panorama editing</span>
          <div class="potree-dialog-header-actions">
            <button class="potree-dialog-pin" aria-label="Pin panel" title="Pin panel" data-i18n="[title]tt.pin_panel;[aria-label]tt.pin_panel">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/></svg>
            </button>
            <button class="potree-dialog-close" aria-label="Close" title="Close" data-i18n="[title]tt.close_panel;[aria-label]tt.close_panel">&#x2715;</button>
          </div>
        </div>
        <div class="potree-dialog-body" style="display:flex;flex-direction:column;overflow:hidden;">
          <div style="flex-shrink:0;">
            <div style="display:flex;align-items:center;justify-content:space-between;padding:10px 14px;border-bottom:1px solid var(--card-border,#333);">
              <span style="font-size:13px;font-weight:500;color:var(--sidebar-text,#eee);" data-i18n="appearance.edit_mode">Edit mode</span>
              <label class="pano-toggle-switch">
                <input type="checkbox" id="pano-edit-mode" />
                <span class="pano-toggle-track"></span>
              </label>
            </div>
            <div id="pano-edit-controls" style="display:none;padding:8px 14px 10px;border-bottom:1px solid var(--card-border,#333);">
              <div style="font-size:11px;font-weight:600;margin:2px 0 4px;opacity:0.6;text-transform:uppercase;letter-spacing:0.5px;" data-i18n="[title]tt.hint_scanner_type;tt.pano_scanner_type" title="">Scanner type</div>
              <div class="pano-ctrl-row">
                <span data-i18n="[title]tt.hint_scanner_type;tt.pano_scanner_type_label" title="">Type</span>
                <select id="pano-scanner-type" title="" data-i18n="[title]tt.hint_scanner_type" style="flex:1;min-width:0;background:var(--color-input-bg,#2a2a2a);color:inherit;border:1px solid var(--color-border,#444);border-radius:3px;padding:2px 4px;font-size:11px;cursor:pointer;">
                  <option value="auto" data-i18n="tt.pano_scanner_auto">Auto</option>
                  <option value="navvis" data-i18n="tt.pano_scanner_type_1">Type 1</option>
                  <option value="leica" data-i18n="tt.pano_scanner_type_2">Type 2</option>
                  <option value="faro" data-i18n="tt.pano_scanner_type_3">Type 3</option>
                  <option value="recap" data-i18n="tt.pano_scanner_type_4">Type 4</option>
                  <option value="fjd" data-i18n="tt.pano_scanner_type_5">Type 5</option>
                  <option value="other" data-i18n="tt.pano_scanner_other">Other</option>
                </select>
              </div>
              <div style="font-size:11px;font-weight:600;margin:8px 0 4px;opacity:0.6;text-transform:uppercase;letter-spacing:0.5px;" data-i18n="tt.pano_rotation">Rotation</div>
              <div class="pano-ctrl-row"><span>Roll</span><input type="range" id="pano-rot-roll" min="-180" max="180" step="0.1" value="0" /><input type="number" id="pano-rot-roll-num" step="0.1" value="0" style="width:52px;" /></div>
              <div class="pano-ctrl-row"><span>Pitch</span><input type="range" id="pano-rot-pitch" min="-180" max="180" step="0.1" value="0" /><input type="number" id="pano-rot-pitch-num" step="0.1" value="0" style="width:52px;" /></div>
              <div class="pano-ctrl-row"><span>Course</span><input type="range" id="pano-rot-course" min="-180" max="180" step="0.1" value="0" /><input type="number" id="pano-rot-course-num" step="0.1" value="0" style="width:52px;" /></div>
              <div style="font-size:11px;font-weight:600;margin:8px 0 4px;opacity:0.6;text-transform:uppercase;letter-spacing:0.5px;" data-i18n="tt.pano_position">Position</div>
              <div class="pano-ctrl-row"><span>X</span><input type="range" id="pano-pos-x" min="-100" max="100" step="0.1" value="0" /><input type="number" id="pano-pos-x-num" step="0.1" value="0" style="width:52px;" /></div>
              <div class="pano-ctrl-row"><span>Y</span><input type="range" id="pano-pos-y" min="-100" max="100" step="0.1" value="0" /><input type="number" id="pano-pos-y-num" step="0.1" value="0" style="width:52px;" /></div>
              <div class="pano-ctrl-row"><span>Z</span><input type="range" id="pano-pos-z" min="-100" max="100" step="0.1" value="0" /><input type="number" id="pano-pos-z-num" step="0.1" value="0" style="width:52px;" /></div>
              <div style="font-size:11px;font-weight:600;margin:8px 0 4px;opacity:0.6;text-transform:uppercase;letter-spacing:0.5px;" data-i18n="appearance.edl_opacity">Opacity</div>
              <div class="pano-ctrl-row"><span>EDL</span><input type="range" id="pano-edl-opacity" min="0" max="1" step="0.01" value="0.8" /><input type="number" id="pano-edl-opacity-num" step="0.01" min="0" max="1" value="0.8" style="width:52px;" /></div>
              <div style="margin-top:8px;display:flex;gap:6px;">
                <button id="pano-save-btn" style="flex:1;padding:6px 0;border:1px solid var(--card-border,#444);border-radius:var(--btn-radius,6px);background:var(--brand-primary,#0097b2);color:#fff;font-size:12px;font-weight:500;cursor:pointer;transition:background 0.15s,box-shadow 0.15s;" data-i18n="tt.pano_save">Save</button>
                <button id="pano-exit-btn" style="padding:6px 10px;border:1px solid var(--card-border,#444);border-radius:var(--btn-radius,6px);background:var(--sidebar-bg,#1e1e1e);color:var(--sidebar-text,#eee);font-size:12px;font-weight:500;cursor:pointer;transition:background 0.15s;" title="Exit panorama (Esc)" data-i18n="[title]tt.pano_exit;[aria-label]tt.pano_exit">&#x2715;</button>
              </div>
            </div>
          </div>
          <ul class="content-panel-list" id="pano-edit-list" style="flex:1;overflow-y:auto;margin:0;"></ul>
        </div>
        <div id="pano-save-all-footer" style="display:none;flex-shrink:0;padding:6px 10px;border-top:1px solid var(--color-border,#444);">
          <button class="content-panel-footer-btn" id="pano-save-all-btn" style="width:100%;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:4px;"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
            <span data-i18n="tt.pano_save_all">Save all</span>
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const pinBtn = overlay.querySelector('.potree-dialog-pin');
    const closePanel = (keepPin = false) => {
      if (!keepPin) this._panelPin = false;
      overlay.classList.remove('is-open', 'is-pinned');
      pinBtn.classList.remove('active');
    };
    this._registerPanel(overlay, closePanel, pinBtn);

    let justOpened = false;
    overlay.querySelector('.potree-dialog-close').addEventListener('click', () => closePanel());
    document.addEventListener('click', (e) => {
      if (!overlay.classList.contains('is-open')) return;
      if (justOpened) return;
      if (this._panelPin) return;
      if (overlay.querySelector('.potree-dialog').contains(e.target)) return;
      // Don't close when clicking inside the 3D render area (e.g. switching panoramas)
      const renderArea = document.getElementById('potree_render_area');
      if (renderArea && renderArea.contains(e.target)) return;
      closePanel();
    }, true);

    // Edit mode checkbox
    const editModeChk = document.getElementById('pano-edit-mode');
    editModeChk.addEventListener('change', () => {
      viewer.dispatchEvent({
        type: 'panorama_edit_mode_changed',
        value: editModeChk.checked,
      });
    });

    // Keep checkbox in sync if edit mode changes from elsewhere
    viewer.addEventListener('panorama_edit_mode_changed', (e) => {
      editModeChk.checked = !!e.value;
    });

    // Inject scoped CSS for control rows
    if (!document.getElementById('pano-ctrl-style')) {
      const style = document.createElement('style');
      style.id = 'pano-ctrl-style';
      style.textContent = `
        .pano-ctrl-row { display:flex; align-items:center; gap:4px; margin:2px 0; font-size:11px; }
        .pano-ctrl-row span { width:42px; flex-shrink:0; opacity:0.7; }
        .pano-ctrl-row input[type=range] { flex:1; min-width:0; height:4px; accent-color:var(--color-accent,#0097b2); }
        .pano-ctrl-row input[type=number] { background:var(--color-input-bg,#2a2a2a); color:inherit; border:1px solid var(--color-border,#444); border-radius:3px; padding:1px 4px; font-size:11px; }
        #pano-edit-list .content-panel-item:hover { background:var(--color-hover-bg,rgba(255,255,255,0.05)); }
        .pano-toggle-switch { position:relative; display:inline-block; width:36px; height:20px; cursor:pointer; }
        .pano-toggle-switch input { opacity:0; width:0; height:0; position:absolute; }
        .pano-toggle-track { position:absolute; inset:0; background:var(--card-border,#444); border-radius:10px; transition:background 0.2s; }
        .pano-toggle-track::after { content:''; position:absolute; top:2px; left:2px; width:16px; height:16px; background:#fff; border-radius:50%; transition:transform 0.2s; }
        .pano-toggle-switch input:checked + .pano-toggle-track { background:var(--brand-primary,#0097b2); }
        .pano-toggle-switch input:checked + .pano-toggle-track::after { transform:translateX(16px); }
      `;
      document.head.appendChild(style);
    }

    // ── Edit controls (rotation/position/save) ──
    const editControlsDiv = document.getElementById('pano-edit-controls');
    // Prefer the Images360 set whose panorama is currently focused so the
    // edit panel (sliders, scanner type dropdown) operates on the right set.
    const getImages360 = () =>
      scene.images360.find((i) => i.focusedImage) || scene.images360[0] || null;

    // Scanner type dropdown — value reflects currently focused set.
    // pendingScannerTypes tracks user selections not yet committed to DB
    // (commit happens when the user clicks Save / Save All).
    const pendingScannerTypes = new Map(); // uploadId → pendingType

    const scannerTypeSelect = document.getElementById('pano-scanner-type');
    scannerTypeSelect.addEventListener('change', () => {
      const img360 = getImages360();
      if (!img360) return;
      pendingScannerTypes.set(img360.uploadId, scannerTypeSelect.value);
      viewer.dispatchEvent({
        type: 'panorama_scanner_type_changed',
        value: scannerTypeSelect.value,
        images360: img360,
      });
    });

    // Commit pending scanner type for a given images360 set if one exists.
    const commitScannerTypeIfPending = (img360) => {
      if (!img360 || !img360.uploadId) return;
      const pending = pendingScannerTypes.get(img360.uploadId);
      if (pending === undefined) return;
      pendingScannerTypes.delete(img360.uploadId);
      viewer.dispatchEvent({
        type: 'panorama_scanner_type_commit',
        value: pending,
        images360: img360,
      });
    };

    const syncScannerTypeDropdown = () => {
      const img360 = getImages360();
      if (!img360) return;
      // Prefer the pending (preview) value so the dropdown stays in sync after reload.
      const pending = img360.uploadId ? pendingScannerTypes.get(img360.uploadId) : undefined;
      const t = pending ?? img360.scannerType ?? 'auto';
      if (scannerTypeSelect.value !== t) {
        scannerTypeSelect.value = t;
      }
      // Disable when the focused set isn't an E57-derived upload (no uploadId)
      scannerTypeSelect.disabled = !img360.uploadId;
    };

    const sliders = {
      roll:   { range: document.getElementById('pano-rot-roll'),   num: document.getElementById('pano-rot-roll-num') },
      pitch:  { range: document.getElementById('pano-rot-pitch'),  num: document.getElementById('pano-rot-pitch-num') },
      course: { range: document.getElementById('pano-rot-course'), num: document.getElementById('pano-rot-course-num') },
      posX:   { range: document.getElementById('pano-pos-x'),      num: document.getElementById('pano-pos-x-num') },
      posY:   { range: document.getElementById('pano-pos-y'),      num: document.getElementById('pano-pos-y-num') },
      posZ:   { range: document.getElementById('pano-pos-z'),      num: document.getElementById('pano-pos-z-num') },
    };

    const saveBtnEl = document.getElementById('pano-save-btn');

    const resetSliders = () => {
      for (const s of Object.values(sliders)) {
        s.range.value = 0;
        s.num.value = 0;
      }
    };

    const syncSlidersToImage = () => {
      const img360 = getImages360();
      if (!img360 || !img360.focusedImage) { resetSliders(); return; }
      const d = img360._pendingDeltas.get(img360.focusedImage);
      if (!d) { resetSliders(); return; }
      sliders.roll.range.value = d.roll; sliders.roll.num.value = d.roll;
      sliders.pitch.range.value = d.pitch; sliders.pitch.num.value = d.pitch;
      sliders.course.range.value = d.course; sliders.course.num.value = d.course;
      sliders.posX.range.value = d.posX; sliders.posX.num.value = d.posX;
      sliders.posY.range.value = d.posY; sliders.posY.num.value = d.posY;
      sliders.posZ.range.value = d.posZ; sliders.posZ.num.value = d.posZ;
    };

    const syncRotation = (type, value) => {
      const img360 = getImages360();
      if (!img360) return;
      let val = parseFloat(value) || 0;
      if (val > 180) val = 180;
      if (val < -180) val = -180;

      sliders[type].range.value = val;
      sliders[type].num.value = val;

      viewer.dispatchEvent({ type: 'panorama_rotation_changed', axis: type, value: val });
    };

    const syncPosition = (axis, value) => {
      const key = 'pos' + axis;
      let val = parseFloat(value) || 0;
      sliders[key].range.value = val;
      sliders[key].num.value = val;

      viewer.dispatchEvent({ type: 'panorama_position_changed', axis, value: val });
    };

    for (const type of ['roll', 'pitch', 'course']) {
      sliders[type].range.addEventListener('input', () => syncRotation(type, sliders[type].range.value));
      sliders[type].num.addEventListener('input', () => syncRotation(type, sliders[type].num.value));
    }
    for (const axis of ['X', 'Y', 'Z']) {
      const key = 'pos' + axis;
      sliders[key].range.addEventListener('input', () => syncPosition(axis, sliders[key].range.value));
      sliders[key].num.addEventListener('input', () => syncPosition(axis, sliders[key].num.value));
    }

    // EDL opacity slider in panorama edit panel
    const edlOpacityRange = document.getElementById('pano-edl-opacity');
    const edlOpacityNum = document.getElementById('pano-edl-opacity-num');

    const syncEdlOpacity = (val) => {
      val = Math.max(0, Math.min(1, parseFloat(val) || 0));
      edlOpacityRange.value = val;
      edlOpacityNum.value = val;
      viewer.setEDLOpacity(val);
    };

    edlOpacityRange.addEventListener('input', () => syncEdlOpacity(edlOpacityRange.value));
    edlOpacityNum.addEventListener('input', () => syncEdlOpacity(edlOpacityNum.value));

    viewer.addEventListener('edl_opacity_changed', () => {
      const v = viewer.getEDLOpacity();
      edlOpacityRange.value = v;
      edlOpacityNum.value = v;
    });

    saveBtnEl.addEventListener('click', () => {
      const img360 = getImages360();
      if (img360) {
        img360.acceptRotation();
        commitScannerTypeIfPending(img360);
      }
      resetSliders();
      renderList();
    });

    document.getElementById('pano-exit-btn').addEventListener('click', () => {
      const img360 = getImages360();
      if (img360) img360.unfocus();
    });

    // Show/hide edit controls based on focus state
    const updateEditControlsVisibility = () => {
      const img360 = getImages360();
      const isFocused = img360 && img360.focusedImage;
      const isEditMode = editModeChk.checked;
      editControlsDiv.style.display = (isFocused && isEditMode) ? '' : 'none';
    };

    // Poll focus state via viewer update (panorama focus/unfocus don't fire a specific event)
    let prevFocused = null;
    viewer.addEventListener('update', () => {
      const img360 = getImages360();
      // Skip null state during direct panorama-to-panorama switch (avoids flicker)
      if (img360 && img360._switchingPanoramas) return;
      const curFocused = img360 ? img360.focusedImage : null;
      if (curFocused !== prevFocused) {
        prevFocused = curFocused;
        syncSlidersToImage();
        syncScannerTypeDropdown();
        updateEditControlsVisibility();
        renderList();
      }
    });

    // Host code can announce that a set's scanner type changed externally
    // (e.g. after auto-detection sync from backend) and we just re-read.
    scene.addEventListener('360_images_added', syncScannerTypeDropdown);

    viewer.addEventListener('panorama_edit_mode_changed', updateEditControlsVisibility);
    viewer.addEventListener('panorama_deltas_changed', () => { renderList(); });

    // ── Panorama list ──
    const list = document.getElementById('pano-edit-list');
    const saveAllFooter = document.getElementById('pano-save-all-footer');
    const saveAllBtn = document.getElementById('pano-save-all-btn');

    const getShortName = (file) => {
      let text = String(file);
      if (text.lastIndexOf('./') === -1) {
        text = text.slice(text.lastIndexOf('/') + 1);
      } else {
        text = text.split('./').pop();
      }
      return text;
    };

    const renderList = () => {
      list.innerHTML = '';
      const allImages360 = scene.images360;
      if (!allImages360 || allImages360.length === 0) return;

      let hasPending = false;

      const showGroupHeaders = allImages360.length > 1;
      for (const images of allImages360) {
        if (showGroupHeaders) {
          const header = document.createElement('li');
          header.style.cssText = 'padding:6px 8px 2px;font-size:10px;font-weight:600;opacity:0.6;text-transform:uppercase;letter-spacing:0.5px;';
          header.textContent = images.displayName || images.name || 'Panorama';
          list.appendChild(header);
        }
        const focused = images.focusedImage;
        for (const image of images.images) {
          const li = document.createElement('li');
          li.className = 'content-panel-item';
          const isFocused = (image === focused);
          const isSaved = images.isImageAccepted(image);
          const isPending = images.hasPendingDelta(image);
          if (isPending) hasPending = true;
          li.style.cssText = 'display:flex;align-items:center;gap:6px;padding:4px 8px;'
            + (isFocused ? 'background:var(--color-accent-bg,rgba(0,151,178,0.15));' : '');

          const dot = document.createElement('span');
          const dotColor = isPending ? '#fb8c00' : (isSaved ? '#43a047' : 'var(--color-border,#555)');
          const dotTitle = isPending ? 'Unsaved changes' : (isSaved ? 'Saved' : 'Not modified');
          dot.style.cssText = 'width:8px;height:8px;border-radius:50%;flex-shrink:0;background:' + dotColor + ';';
          dot.title = dotTitle;

          const nameBtn = document.createElement('button');
          nameBtn.className = 'content-panel-footer-btn';
          nameBtn.style.cssText = 'flex:1;text-align:left;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:11px;'
            + (isFocused ? 'font-weight:600;color:var(--color-accent,#0097b2);' : '');
          nameBtn.textContent = getShortName(image.file);
          nameBtn.title = image.file;
          nameBtn.addEventListener('click', () => {
            images.focusList(image);
          });

          li.appendChild(dot);
          li.appendChild(nameBtn);
          list.appendChild(li);
        }
      }

      saveAllFooter.style.display = hasPending ? '' : 'none';
    };

    saveAllBtn.addEventListener('click', async () => {
      const img360 = getImages360();
      if (!img360) return;
      saveAllBtn.disabled = true;
      saveAllBtn.textContent = 'Saving…';
      try {
        if (img360.focusedImage) {
          await img360.acceptRotation();
          resetSliders();
        }
        await img360.saveAll();
        commitScannerTypeIfPending(img360);
      } finally {
        saveAllBtn.disabled = false;
        saveAllBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:4px;"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1-2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>Save all';
        renderList();
      }
    });

    scene.addEventListener('360_images_added', renderList);

    // Show/hide topbar menu item based on permissions + panoramas loaded
    const showPanoEditIfAllowed = () => {
      const menuItem = document.getElementById('topbar-panorama-edit');
      if (!menuItem) return;
      const hasPanos = scene.images360.length > 0;
      const canEdit = viewer.canEditPanoramas || false;
      menuItem.style.display = (hasPanos && canEdit) ? '' : 'none';
    };
    scene.addEventListener('360_images_added', showPanoEditIfAllowed);
    viewer.addEventListener('edit_rights_changed', showPanoEditIfAllowed);

    return () => {
      renderList();
      this._beforeOpenPanel(overlay);
      $(overlay).i18n();
      overlay.classList.add('is-open');
      justOpened = true;
      setTimeout(() => { justOpened = false; }, 0);
    };
  }

  initClassificationPanel() {
    const viewer = this.viewer;

    const overlay = document.createElement('div');
    overlay.id = 'classification-panel-overlay';
    overlay.className = 'potree-dialog-overlay no-backdrop';
    overlay.innerHTML = `
      <div class="potree-dialog" role="dialog" aria-modal="true">
        <div class="potree-dialog-header">
          <span class="potree-dialog-title" data-i18n="menu.classification">Classification</span>
          <div class="potree-dialog-header-actions">
            <button class="potree-dialog-pin" aria-label="Pin panel" title="Pin panel" data-i18n="[title]tt.pin_panel;[aria-label]tt.pin_panel">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/></svg>
            </button>
            <button class="potree-dialog-close" aria-label="Close" title="Close" data-i18n="[title]tt.close_panel;[aria-label]tt.close_panel">&#x2715;</button>
          </div>
        </div>
        <div class="potree-dialog-body">
          <ul class="classification-panel-list"></ul>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const pinBtn = overlay.querySelector('.potree-dialog-pin');
    let justOpened = false;

    const closePanel = (keepPin = false) => {
      if (!keepPin) this._panelPin = false;
      overlay.classList.remove('is-open', 'is-pinned');
      if (pinBtn) pinBtn.classList.remove('active');
    };

    this._registerPanel(overlay, closePanel, pinBtn);

    overlay.querySelector('.potree-dialog-close').addEventListener('click', () => closePanel());
    document.addEventListener('click', (e) => {
      if (!overlay.classList.contains('is-open')) return;
      if (justOpened) return;
      if (this._panelPin) return;
      if (overlay.querySelector('.potree-dialog').contains(e.target)) return;
      closePanel();
    }, true);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
        closePanel();
      }
    });

    const list = overlay.querySelector('.classification-panel-list');

    const render = () => {
      // Remove old spectrum instances before clearing
      $(list).find('input[type=text]').spectrum('destroy');
      list.innerHTML = '';

      // Toggle-all row
      const toggleLi = document.createElement('li');
      toggleLi.className = 'classification-panel-item';
      toggleLi.innerHTML = `
        <label class="content-panel-label">
          <input id="dlg_toggleAllClassifications" type="checkbox" />
          <span class="content-panel-name" data-i18n="tt.show_hide_all">show / hide all</span>
        </label>
      `;
      const toggleCb = toggleLi.querySelector('input');
      const allVisible = Object.values(viewer.classifications).every(c => c.visible);
      toggleCb.checked = allVisible;
      toggleCb.addEventListener('click', () => viewer.toggleAllClassificationsVisibility());
      list.appendChild(toggleLi);

      // Per-classification rows
      for (const [code, cls] of Object.entries(viewer.classifications)) {
        const li = document.createElement('li');
        li.className = 'classification-panel-item';
        const colorId = `dlg_clr_${code}`;
        li.innerHTML = `
          <label class="content-panel-label">
            <input id="dlg_chk_cls_${code}" type="checkbox" ${cls.visible ? 'checked' : ''} />
            <span class="content-panel-name">${cls.name}</span>
          </label>
          <input id="${colorId}" type="text" class="classification-color-picker" />
        `;

        li.querySelector(`#dlg_chk_cls_${code}`).addEventListener('click', (e) => {
          viewer.setClassificationVisibility(code, e.target.checked);
        });

        const defaultColor = `rgb(${cls.color.slice(0, 3).map(c => Math.round(c * 255)).join(', ')})`;
        $(`#${colorId}`, li).spectrum({
          color: defaultColor,
          showInput: true,
          preferredFormat: 'rgb',
          cancelText: '',
          chooseText: 'Apply',
          move: (color) => {
            const { r, g, b } = color.toRgb();
            cls.color = [r / 255, g / 255, b / 255, 1];
          },
          change: (color) => {
            const { r, g, b } = color.toRgb();
            cls.color = [r / 255, g / 255, b / 255, 1];
          },
        });

        list.appendChild(li);
      }

      // Invert button
      const invertLi = document.createElement('li');
      invertLi.className = 'classification-panel-item';
      invertLi.innerHTML = `<button class="classification-invert-btn" data-i18n="tt.invert">Invert</button>`;
      invertLi.querySelector('button').addEventListener('click', () => {
        for (const [key, val] of Object.entries(viewer.classifications)) {
          viewer.setClassificationVisibility(key, !val.visible);
        }
      });
      list.appendChild(invertLi);
    };

    const syncCheckboxes = () => {
      for (const [code, cls] of Object.entries(viewer.classifications)) {
        const cb = list.querySelector(`#dlg_chk_cls_${code}`);
        if (cb) cb.checked = cls.visible;
      }
      const numVisible = Object.values(viewer.classifications).filter(c => c.visible).length;
      const numTotal = Object.keys(viewer.classifications).length;
      const toggle = list.querySelector('#dlg_toggleAllClassifications');
      if (toggle) toggle.checked = (numVisible === numTotal);
    };

    viewer.addEventListener('classifications_changed', render);
    viewer.addEventListener('classification_visibility_changed', syncCheckboxes);

    this._openClassificationPanel = () => {
      this._beforeOpenPanel(overlay);
      render();
      $(overlay).i18n();
      overlay.classList.add('is-open');
      justOpened = true;
      setTimeout(() => { justOpened = false; }, 0);
    };
  }

  _createToolHintElement() {
    const el = document.createElement('div');
    el.id = 'potree_tool_hint';
    el.innerHTML = `
      <div class="tool-hint-row">
        <span class="tool-hint-title"></span>
        <span class="tool-hint-desc"></span>
      </div>
      <div class="tool-hint-row">
        <span class="tool-hint-controls"></span>
        <span class="tool-hint-after"></span>
      </div>
    `;
    return el;
  }

  _showToolHint(toolKey, { instant = false } = {}) {
    if (localStorage.getItem('potree-tool-hints') === 'off') return;

    if (!this._toolHintEl) {
      this._toolHintEl = this._createToolHintElement();
      this.viewer.renderArea.appendChild(this._toolHintEl);
    }

    const el = this._toolHintEl;
    el.querySelector('.tool-hint-title').textContent = i18n.t(`tool_hints.${toolKey}_title`, toolKey);
    el.querySelector('.tool-hint-desc').textContent = i18n.t(`tool_hints.${toolKey}_desc`, '');
    el.querySelector('.tool-hint-controls').innerHTML = i18n.t(`tool_hints.${toolKey}_controls`, '');

    const afterText = i18n.t(`tool_hints.${toolKey}_after`, '');
    const afterEl = el.querySelector('.tool-hint-after');
    if (afterText) {
      afterEl.innerHTML = afterText;
      afterEl.style.display = '';
    } else {
      afterEl.innerHTML = '';
      afterEl.style.display = 'none';
    }

    // Cancel any pending dismiss timer
    if (this._toolHintTimer) {
      clearTimeout(this._toolHintTimer);
      this._toolHintTimer = null;
    }

    el.classList.add('is-visible');

    // Instant-action tools auto-dismiss after a short time;
    // interactive tools stay visible until _clearActiveTools() is called
    if (instant) {
      this._toolHintTimer = setTimeout(() => this._hideToolHint(), 2500);
    }
  }

  /**
   * Wire snap source / BIM mode controls inside a root element.
   * @param {HTMLElement} root
   * @param {string} radioName  unique name for BIM mode radios
   */
  _wireMeasurementSnapControls(root, radioName) {
    const viewer = this.viewer;
    if (!viewer.measurementSnapSources) {
      viewer.measurementSnapSources = {
        pointCloud: true,
        realityMesh: true,
        bim: true,
      };
    }
    if (!viewer.measurementBimSnapMode) {
      viewer.measurementBimSnapMode = 'surface';
    }

    root.querySelectorAll('input[name="__bim_snap_mode__"]').forEach((input) => {
      input.name = radioName;
    });

    const snap = viewer.measurementSnapSources;
    const modeRadios = root.querySelectorAll('.measure-snap-mode');
    const modeGroup = root.querySelector('.measure-snap-modes');

    const syncBimModeEnabled = () => {
      const enabled = snap.bim !== false;
      if (modeGroup) modeGroup.classList.toggle('is-disabled', !enabled);
      modeRadios.forEach((input) => { input.disabled = !enabled; });
    };

    root.querySelectorAll('[data-snap]').forEach((input) => {
      const key = input.dataset.snap;
      input.checked = snap[key] !== false;
      input.addEventListener('change', () => {
        snap[key] = input.checked;
        if (key === 'bim') syncBimModeEnabled();
      });
    });

    const currentMode = viewer.measurementBimSnapMode;
    modeRadios.forEach((input) => {
      input.checked = input.value === currentMode;
      input.addEventListener('change', () => {
        if (input.checked) viewer.measurementBimSnapMode = input.value;
      });
    });
    syncBimModeEnabled();
  }

  /**
   * Gear next to the Measurements panel pin — opens snap source / BIM mode flyout.
   * @param {HTMLElement} overlay
   * @returns {HTMLElement} flyout element
   */
  _attachMeasurementSnapHeaderButton(overlay) {
    const trigger = overlay.querySelector('.potree-dialog-snap');
    if (!trigger) return null;

    const flyout = document.createElement('div');
    flyout.className = 'measure-snap-flyout';
    flyout.hidden = true;
    flyout.setAttribute('role', 'dialog');
    flyout.setAttribute('aria-label', 'Measurement snap');
    flyout.innerHTML = `
      <div class="measure-snap-sources" role="group">
        <span class="measure-snap-heading" data-i18n="tt.snap_to">Snap to</span>
        <label class="measure-snap-option">
          <input type="checkbox" data-snap="pointCloud">
          <span data-i18n="tt.snap_point_cloud">Point cloud</span>
        </label>
        <label class="measure-snap-option">
          <input type="checkbox" data-snap="realityMesh">
          <span data-i18n="tt.snap_reality_mesh">Mesh</span>
        </label>
        <label class="measure-snap-option">
          <input type="checkbox" data-snap="bim">
          <span data-i18n="tt.snap_bim">BIM</span>
        </label>
      </div>
      <div class="measure-snap-modes" role="radiogroup">
        <span class="measure-snap-heading" data-i18n="tt.snap_bim_mode">BIM mode</span>
        <label class="measure-snap-option">
          <input type="radio" name="__bim_snap_mode__" value="point" class="measure-snap-mode">
          <span data-i18n="tt.snap_mode_point">Point</span>
        </label>
        <label class="measure-snap-option">
          <input type="radio" name="__bim_snap_mode__" value="line" class="measure-snap-mode">
          <span data-i18n="tt.snap_mode_line">Line</span>
        </label>
        <label class="measure-snap-option">
          <input type="radio" name="__bim_snap_mode__" value="surface" class="measure-snap-mode">
          <span data-i18n="tt.snap_mode_surface">Surface</span>
        </label>
      </div>
    `;
    document.body.appendChild(flyout);
    this._wireMeasurementSnapControls(flyout, 'panel-bim-snap-mode');

    const positionFlyout = () => {
      const rect = trigger.getBoundingClientRect();
      const width = flyout.offsetWidth || 200;
      flyout.style.top = `${rect.bottom + 6}px`;
      flyout.style.left = `${Math.max(8, rect.right - width)}px`;
    };

    const close = () => {
      flyout.hidden = true;
      flyout.classList.remove('is-open');
      trigger.classList.remove('active');
      trigger.setAttribute('aria-expanded', 'false');
    };

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      const opening = !flyout.classList.contains('is-open');
      close();
      if (opening) {
        // Unhide before measuring — [hidden] is display:none, so offsetWidth
        // is 0 until then. Opacity stays 0 until .is-open is added.
        flyout.hidden = false;
        positionFlyout();
        flyout.classList.add('is-open');
        trigger.classList.add('active');
        trigger.setAttribute('aria-expanded', 'true');
      }
    });

    flyout.addEventListener('click', (e) => e.stopPropagation());
    document.addEventListener('click', (e) => {
      if (!flyout.classList.contains('is-open')) return;
      if (flyout.contains(e.target) || trigger.contains(e.target)) return;
      close();
    }, true);

    return flyout;
  }

  initToolbar() {
    let elToolbar = $('#tools');

    const openMeasurementsSidebar = () => {
      this._contentPanels?.['open-measurements-panel']?.();
    };

    const addTool = (icon, title, toolKey, callback) => {
      const el = this.createToolIcon(Potree.resourcePath + '/icons/' + icon, title, null);
      el.off('click').on('click', () => {
        this._clearActiveTools();
        callback();
        // A dimmed tool's callback only explains why it is unavailable;
        // don't highlight it or open its hint as if it had armed.
        if (el.hasClass('is-disabled')) return;
        el[0].classList.add('active');
        if (toolKey) this._showToolHint(toolKey);
      });
      elToolbar.append(el);
      return el;
    };

    // POINT
    addTool('point.svg', '[title]tt.point_measurement', 'point', () => {
      openMeasurementsSidebar();
      this.measuringTool.startInsertion({ showDistances: false, showAngles: false, showCoordinates: true, showArea: false, closed: true, maxMarkers: 1, name: i18n.t('measurements.name_point', 'Point') });
    });

    // DISTANCE
    addTool('distance.svg', '[title]tt.distance_measurement', 'distance', () => {
      openMeasurementsSidebar();
      this.measuringTool.startInsertion({ showDistances: true, showArea: false, showTotalDistance: true, closed: false, name: i18n.t('measurements.name_distance', 'Distance') });
    });

    // ANGLE
    addTool('angle.svg', '[title]tt.angle_measurement', 'angle', () => {
      openMeasurementsSidebar();
      this.measuringTool.startInsertion({ showDistances: false, showAngles: true, showArea: false, closed: true, maxMarkers: 3, name: i18n.t('measurements.name_angle', 'Angle') });
    });

    // AREA
    addTool('area.svg', '[title]tt.area_measurement', 'area', () => {
      openMeasurementsSidebar();
      this.measuringTool.startInsertion({ showDistances: true, showArea: true, showTotalDistance: false, closed: true, name: i18n.t('measurements.name_area', 'Area') });
    });

    // CIRCLE
    addTool('circle.svg', '[title]tt.circle_measurement', 'circle', () => {
      openMeasurementsSidebar();
      this.measuringTool.startInsertion({ showDistances: false, showHeight: false, showArea: false, showCircle: true, showEdges: false, closed: false, maxMarkers: 3, name: i18n.t('measurements.name_circle', 'Circle') });
    });

    // HEIGHT
    addTool('height.svg', '[title]tt.height_measurement', 'height', () => {
      openMeasurementsSidebar();
      this.measuringTool.startInsertion({ showDistances: false, showHeight: true, showArea: false, closed: false, maxMarkers: 2, name: i18n.t('measurements.name_height', 'Height') });
    });

    // SURFACE
    const surfaceBtn = addTool('surface.svg', '[title]tt.surface_measurement', 'surface', () => {
      openMeasurementsSidebar();
      this.viewer.surfaceTool.startInsertion();
    });
    const defaultSurfaceTitle = surfaceBtn.attr('data-i18n');
    const syncSurfaceTool = () => {
      const has = (this.viewer.scene.pointclouds || []).length > 0;
      const atCap = !canAddSurface(this.viewer.scene);
      // A visible cloud without the occupancy sidecar (or a legacy cloud
      // with no backend id) would only make /surfaces/grow fail — dim the
      // tool and say why, instead of letting the user discover it by error.
      const blockers = surfaceIndexBlockers(this.viewer.scene);
      const disabled = atCap || blockers.length > 0;
      surfaceBtn.toggle(has);
      surfaceBtn.css('opacity', disabled ? 0.4 : 1);
      surfaceBtn.toggleClass('is-disabled', disabled);
      if (blockers.length > 0) {
        surfaceBtn.removeAttr('data-i18n').attr('title', surfaceIndexMissingMessage(blockers));
      } else if (atCap) {
        surfaceBtn.removeAttr('data-i18n').attr('title', surfaceLimitMessage());
      } else if (surfaceBtn.attr('data-i18n') !== defaultSurfaceTitle) {
        // Restore the translated default once the block is gone.
        surfaceBtn.attr('data-i18n', defaultSurfaceTitle);
        if (typeof surfaceBtn.i18n === 'function') surfaceBtn.i18n();
      }
    };
    // Visibility toggles in the Content panel decide which clouds count.
    const watchCloudVisibility = (e) => {
      e.pointcloud?.addEventListener?.('visibility_changed', syncSurfaceTool);
      syncSurfaceTool();
    };
    const unwatchCloudVisibility = (e) => {
      e.pointcloud?.removeEventListener?.('visibility_changed', syncSurfaceTool);
      syncSurfaceTool();
    };
    this.viewer.scene.addEventListener('pointcloud_added', watchCloudVisibility);
    this.viewer.scene.addEventListener('pointcloud_removed', unwatchCloudVisibility);
    this.viewer.scene.addEventListener('surface_added', syncSurfaceTool);
    this.viewer.scene.addEventListener('surface_removed', syncSurfaceTool);
    for (const pc of this.viewer.scene.pointclouds || []) {
      pc.addEventListener?.('visibility_changed', syncSurfaceTool);
    }
    // Tooltips built from i18n strings must follow a language switch.
    this.viewer.addEventListener('language_changed', syncSurfaceTool);
    syncSurfaceTool();

    // PROFILE
    addTool('profile.svg', '[title]tt.height_profile', 'profile', () => {
      openMeasurementsSidebar();
      this.profileTool.startInsertion({ name: i18n.t('measurements.name_profile', 'Profile') });
    });

    // ANNOTATION
    addTool('annotation.svg', '[title]tt.annotation', 'annotation', () => {
      openMeasurementsSidebar();
      this.viewer.annotationTool.startInsertion();
    });

    // ── Clipping ──
    this._openClippingPanel = () => this._contentPanels?.['open-clipping-panel']?.();

    addTool('clip_volume.svg', '[title]tt.clip_volume', 'clip_volume', () => {
      const viewer = this.viewer;

      // The clip task is global: with Inside/Outside active most of the cloud is hidden,
      // so a new box would be placed blind. Show everything while placing, restore after.
      const previousClipTask = this._clipTaskBeforeInsertion ?? viewer.getClipTask();

      let item = this.volumeTool.startInsertion({ clip: true });
      item.name = i18n.t('measurements.name_clip_volume', 'Clip volume');

      viewer.setClipTask(ClipTask.HIGHLIGHT);

      if (previousClipTask !== ClipTask.HIGHLIGHT) {
        this._clipTaskBeforeInsertion = previousClipTask;

        const finish = (restore) => {
          viewer.removeEventListener('cancel_insertions', onInsertionEnd);
          viewer.removeEventListener('cliptask_changed', onManualChange);
          this._clipTaskBeforeInsertion = null;
          if (restore) viewer.setClipTask(previousClipTask);
        };
        const onInsertionEnd = () => finish(true);
        const onManualChange = () => finish(false);

        viewer.addEventListener('cancel_insertions', onInsertionEnd);
        viewer.addEventListener('cliptask_changed', onManualChange);
      }

      item.addEventListener('drop', () => {
        this._clearActiveTools();
        this._contentPanels['open-clipping-panel'](item);
      }, { once: true });
    });

    // ── Navigation ──

    // Focus
    const elFocus = this.createToolIcon(Potree.resourcePath + '/icons/focus.svg', '[title]tt.focus_control', () => {
      this.viewer.fitToScreen();
      this._showToolHint('focus', { instant: true });
    });
    elToolbar.append(elFocus);

    // View directions (expandable group)
    {
      const viewDefs = [
        { icon: 'left.svg',   i18n: '[title]tt.left_view_control',   action: () => this.viewer.setLeftView() },
        { icon: 'right.svg',  i18n: '[title]tt.right_view_control',  action: () => this.viewer.setRightView() },
        { icon: 'front.svg',  i18n: '[title]tt.front_view_control',  action: () => this.viewer.setFrontView() },
        { icon: 'back.svg',   i18n: '[title]tt.back_view_control',   action: () => this.viewer.setBackView() },
        { icon: 'top.svg',    i18n: '[title]tt.top_view_control',    action: () => this.viewer.setTopView() },
        { icon: 'bottom.svg', i18n: '[title]tt.bottom_view_control', action: () => this.viewer.setBottomView() },
      ];

      const container = $('<div class="nav-views-group"></div>');
      const trigger = this.createToolIcon(
        Potree.resourcePath + '/icons/front.svg',
        '[title]tt.set_view_control',
        null
      );
      trigger.addClass('nav-views-trigger');

      const flyout = $('<div class="nav-views-flyout"></div>');
      viewDefs.forEach(v => {
        const btn = this.createToolIcon(Potree.resourcePath + '/icons/' + v.icon, v.i18n, null);
        btn.on('click', () => {
          v.action();
          flyout.removeClass('is-open');
          trigger.removeClass('active');
        });
        flyout.append(btn);
      });

      // Append flyout to body so it escapes sidebar overflow clipping
      $('body').append(flyout);

      const positionFlyout = () => {
        const rect = trigger[0].getBoundingClientRect();
        flyout.css({
          top: rect.top + 'px',
          left: (rect.right + 4) + 'px',
        });
      };

      const close = () => { flyout.removeClass('is-open'); trigger.removeClass('active'); };
      trigger.on('click', (e) => {
        e.stopPropagation();
        const opening = !flyout.hasClass('is-open');
        close();
        if (opening) {
          positionFlyout();
          flyout.addClass('is-open');
          trigger.addClass('active');
        }
      });
      $(document).on('click.nav-views', close);

      container.append(trigger);
      elToolbar.append(container);
    }

  }

  get folders() {
    return this._folders;
  }

  set folders(folders) {
    this._folders = folders;
  }

  initScene() {
    let elScene = $('#menu_scene');
    let elObjects = elScene.next().find('#scene_objects');
    let elProperties = elScene.next().find('#scene_object_properties');

    {
      let elExport = elScene.next().find('#scene_export');

      let dxfIcon = `${Potree.resourcePath}/icons/file_dxf.svg`;
      let potreeIcon = `${Potree.resourcePath}/icons/file_potree.svg`;

      elExport.append(`
				Export: <br>
				<a href="#" download="measure.dxf"><img name="dxf_export_button" src="${dxfIcon}" class="button-icon" style="height: 24px" /></a>
				<a href="#" download="potree.json5"><img name="potree_export_button" src="${potreeIcon}" class="button-icon" style="height: 24px" /></a>
			`);

      let elDownloadDXF = elExport.find('img[name=dxf_export_button]').parent();
      elDownloadDXF.click((event) => {
        let scene = this.viewer.scene;
        let measurements = [
          ...scene.measurements,
          ...scene.profiles,
          ...scene.volumes,
        ];

        if (measurements.length > 0) {
          let dxf = DXFExporter.toString(measurements, this.viewer.measurementDisplayOffset);

          let url = window.URL.createObjectURL(
            new Blob([dxf], { type: 'data:application/octet-stream' })
          );
          elDownloadDXF.attr('href', url);
        } else {
          this.viewer.postError('no measurements to export');
          event.preventDefault();
        }
      });

      let elDownloadPotree = elExport
        .find('img[name=potree_export_button]')
        .parent();
      elDownloadPotree.click((event) => {
        let data = Potree.saveProject(this.viewer);
        let dataString = JSON5.stringify(data, null, '\t');

        let url = window.URL.createObjectURL(
          new Blob([dataString], { type: 'data:application/octet-stream' })
        );
        elDownloadPotree.attr('href', url);
      });
    }

    let propertiesPanel = new PropertiesPanel(elProperties, this.viewer);
    propertiesPanel.setScene(this.viewer.scene);

    let iconClose = exports.resourcePath + '/icons/close.svg';

    localStorage.removeItem('jstree');

    let tree = $(`<div id="jstree_scene"></div>`);

    tree.scene = this.viewer.scene;

    elObjects.append(tree);

    tree.scene = this.viewer.scene;
    tree.measuringTool = this.measuringTool;
    tree.annotationTool = this.viewer.annotationTool;
    tree.sidebar = this;
    tree.viewer = this.viewer;

    function traverseChildren(node, more, sidebar) {
      for (let child of node.children) {
        if (String(child.id).includes('folder')) {
          traverseChildren(child, more, sidebar);
        } else {
          const childNode = $('#annotations').jstree('get_node', child);
          if (childNode.data) {
            childNode.data.folder = new Object({
              id: 'folder_' + sidebar.folders,
              text: '',
            });
            for (let i = more.ref.parents.length - 3; i >= 0; i--) {
              const parentNode = $('#annotations').jstree(
                'get_node',
                more.ref.parents[i]
              );
              childNode.data.folder.text =
                childNode.data.folder.text + parentNode.text + '/';
            }
            childNode.data.folder.text =
              childNode.data.folder.text + more.ref.text;
            childNode.data.folder.text =
              childNode.data.folder.text + '/' + node.text;
          }
        }
      }
    }

    function checkNameDuplicate(children) {
      const baseText = 'New Folder';
      let text = baseText;
      let textArray = [];
      let needsChange = false;
      for (let i = 0; i < children.length; i++) {
        const child = $('#annotations').jstree('get_node', children[i]);
        textArray.push(child.text);
      }
      for (let i = 0; i <= textArray.length; i++) {
        if (i < textArray.length) {
          if (textArray.includes(text)) {
            text = baseText + ' (' + i + ')';
            needsChange = true;
          } else {
            needsChange = false;
          }
        }
      }
      if (needsChange) {
        text = baseText + ' (' + children.length + ')';
      }
      return text;
    }

    //TREE RULES
    tree.jstree({
      plugins: ['checkbox', 'state', 'contextmenu', 'dnd', 'unique'],

      dnd: {
        drop_target: false,
        drag_target: false,
      },
      unique: {
        trim_whitespace: true,
      },
      core: {
        dblclick_toggle: false,
        state: {
          checked: true,
        },
        error: function (error) {
          if (
            error.reason ===
            'Child with name ' +
              error.name +
              ' already exists. Preventing: rename_node'
          ) {
            tree.viewer.postMessage(
              'Folder with this name already exists in this path.',
              {
                duration: 3000,
              }
            );
          }
        },
        check_callback: function (op, node, par, pos, more) {
          if (more && more.dnd) {
            if (
              //annotations inside
              String(node.parents[node.parents.length - 2]).toLowerCase() ===
                'annotations' &&
              ((String(more.ref.id).toLowerCase().includes('folder') &&
                String(
                  more.ref.parents[more.ref.parents.length - 2]
                ).toLowerCase() === 'annotations') ||
                (String(more.ref.id).toLowerCase() === 'annotations' &&
                  more.pos === 'i'))
            ) {
              if (
                !node.id.includes('folder') &&
                more.ref.id.includes('folder')
              ) {
                if (node.data instanceof Annotation) {
                  node.data.folder = new Object({
                    id: 'folder_' + this.folders,
                    text: '',
                  });
                  for (let i = more.ref.parents.length - 3; i >= 0; i--) {
                    const parentNode = $('#annotations').jstree(
                      'get_node',
                      more.ref.parents[i]
                    );
                    node.data.folder.text += parentNode.text + '/';
                  }
                  node.data.folder.text = node.data.folder.text + more.ref.text;
                }
              } else if (node.id.includes('folder')) {
                traverseChildren(node, more, this);
              }

              return true;
            }
            if (
              //annotations outside
              String(node.parent).toLowerCase().includes('folder') &&
              ((String(
                more.ref.parents[more.ref.parents.length - 2]
              ).toLowerCase() === 'annotations' &&
                more.pos !== 'i') ||
                (String(more.ref.id).toLowerCase() === 'annotations' &&
                  more.pos === 'i'))
            ) {
              if (!node.id.includes('folder')) node.data.folder = null;
              return true;
            }
            if (
              //cameraannotations inside
              String(node.parents[node.parents.length - 2]).toLowerCase() ===
                'cameraannotations' &&
              ((String(more.ref.id).toLowerCase().includes('folder') &&
                String(
                  more.ref.parents[more.ref.parents.length - 2]
                ).toLowerCase() === 'cameraannotations') ||
                (String(more.ref.id).toLowerCase() === 'cameraannotations' &&
                  more.pos === 'i'))
            ) {
              if (!node.id.includes('folder')) node.data.folder = more.ref;
              return true;
            }
            if (
              //cameraannotations outside
              String(node.parent).toLowerCase().includes('folder') &&
              ((String(
                more.ref.parents[more.ref.parents.length - 2]
              ).toLowerCase() === 'cameraannotations' &&
                more.pos !== 'i') ||
                (String(more.ref.id).toLowerCase() === 'cameraannotations' &&
                  more.pos === 'i'))
            ) {
              if (!node.id.includes('folder')) node.data.folder = null;
              return true;
            }
            if (
              //measurements inside
              String(node.parents[node.parents.length - 2]).toLowerCase() ===
                'measurements' &&
              ((String(more.ref.id).toLowerCase().includes('folder') &&
                String(
                  more.ref.parents[more.ref.parents.length - 2]
                ).toLowerCase() === 'measurements') ||
                (String(more.ref.id).toLowerCase() === 'measurements' &&
                  more.pos === 'i'))
            ) {
              if (!node.id.includes('folder')) node.data.folder = more.ref;
              return true;
            }
            if (
              //measurements outside
              String(node.parent).toLowerCase().includes('folder') &&
              ((String(
                more.ref.parents[more.ref.parents.length - 2]
              ).toLowerCase() === 'measurements' &&
                more.pos !== 'i') ||
                (String(more.ref.id).toLowerCase() === 'measurements' &&
                  more.pos === 'i'))
            ) {
              if (!node.id.includes('folder')) node.data.folder = null;
              return true;
            }
            return more.pos !== 'i' && par.id == node.parent;
          }
          return true;
        },
        expand_selected_onload: true,
      },
      checkbox: {
        keep_selected_style: true,
        whole_node: false,
        tie_selection: false,
      },
      contextmenu: {
        items: function ($node) {
          {
            return (String(
              $node.parents[$node.parents.length - 2]
            ).toLowerCase() === 'measurements' ||
              String($node.parents[$node.parents.length - 2]).toLowerCase() ===
                'annotations' ||
              String($node.parents[$node.parents.length - 2]).toLowerCase() ===
                'cameraannotations') &&
              !String($node.id).toLowerCase().includes('folder')
              ? {
                  delete: {
                    label: 'Delete',
                    icon: iconClose,
                    action: function (obj) {
                      if ($node.data instanceof Measure) {
                        scene.removeMeasurement($node.data);
                      } else if ($node.data instanceof Profile) {
                        scene.removeProfile($node.data);
                      } else if ($node.data instanceof Volume) {
                        scene.removeVolume($node.data);
                      } else if ($node.data instanceof Annotation) {
                        scene.removeAnnotation($node.data);
                      }
                    },
                  },
                  edit: {
                    label: 'Edit',
                    action: function (obj) {
                      if ($node.data instanceof Measure)
                        tree.measuringTool.continueInsertion($node.data);
                      else if ($node.data instanceof Annotation)
                        tree.annotationTool.continueInsertion($node.data);
                    },
                  },
                }
              : $node.data instanceof CameraAnimation
              ? {
                  delete: {
                    label: 'Delete',
                    icon: iconClose,
                    action: function (obj) {
                      scene.removeCameraAnimation($node.data);
                    },
                  },
                }
              : String($node.id).toLowerCase() === 'annotations' ||
                String($node.id).toLowerCase() === 'cameraannotations' ||
                String($node.id).toLowerCase() === 'measurements'
              ? {
                  create_folder: {
                    label: 'Create Folder',
                    action: function (obj) {
                      const children = $node.children;
                      let text = checkNameDuplicate(children);

                      var newNode = {
                        text: text,
                        children: [],
                        id: 'folder' + tree.sidebar.folders,
                      };

                      let pathString = getPath(newNode, $node);
                      if (String($node.id).toLowerCase() === 'annotations') {
                        tree.sidebar.foldersAnnotationMap.set(
                          newNode.id,
                          pathString
                        );
                        $('#annotations')
                          .jstree()
                          .create_node(
                            $node,
                            newNode,
                            'last',
                            function (new_node) {
                              $('#annotations').jstree('edit', new_node); // Edit node after creation
                            }
                          );
                      } else if (
                        String($node.id).toLowerCase() === 'cameraannotations'
                      ) {
                        tree.sidebar.foldersCameraAMap.set(
                          newNode.id,
                          pathString
                        );
                        $('#cameraannotations')
                          .jstree()
                          .create_node(
                            $node,
                            newNode,
                            'last',
                            function (new_node) {
                              $('#cameraannotations').jstree('edit', new_node); // Edit node after creation
                            }
                          );
                      } else if (
                        String($node.id).toLowerCase() === 'measurements'
                      ) {
                        tree.sidebar.foldersMeasureMap.set(
                          newNode.id,
                          pathString
                        );
                        $('#measurements')
                          .jstree()
                          .create_node(
                            $node,
                            newNode,
                            'last',
                            function (new_node) {
                              $('#measurements').jstree('edit', new_node); // Edit node after creation
                            }
                          );
                      }
                      tree.sidebar.folders = tree.sidebar.folders + 1;
                    },
                  },
                }
              : String($node.id).toLowerCase().includes('folder')
              ? {
                  edit_name: {
                    label: 'Edit Name',
                    action: function (obj) {
                      if (
                        $node.parents[$node.parents.length - 2] ===
                        'annotations'
                      ) {
                        $('#annotations').jstree('edit', $node);
                      } else if (
                        $node.parents[$node.parents.length - 2] ===
                        'cameraannotations'
                      ) {
                        $('#cameraannotations').jstree('edit', $node);
                      } else if (
                        $node.parents[$node.parents.length - 2] ===
                        'measurements'
                      ) {
                        $('#measurements').jstree('edit', $node);
                      }
                    },
                  },
                  delete_folder: {
                    label: 'Delete Folder',
                    action: function (obj) {
                      if (
                        $node.parents[$node.parents.length - 2] ===
                          'annotations' ||
                        $node.parents[$node.parents.length - 2] ===
                          'cameraannotations'
                      ) {
                        for (let child of $node.children_d) {
                          var node = tree.jstree('get_node', child);
                          if (node.data) scene.removeAnnotation(node.data);
                        }
                        for (let child of $node.children_d) {
                          var node = tree.jstree('get_node', child);
                          tree.jstree('delete_node', node);
                        }
                        tree.jstree('delete_node', $node);
                      } else if (
                        $node.parents[$node.parents.length - 2] ===
                        'measurements'
                      ) {
                        for (let child of $node.children_d) {
                          var node = tree.jstree('get_node', child);
                          if (node.data) scene.removeMeasurement(node.data);
                        }
                        for (let child of $node.children_d) {
                          var node = tree.jstree('get_node', child);
                          tree.jstree('delete_node', node);
                        }

                        tree.jstree('delete_node', $node);
                      }
                    },
                  },
                  create_folder: {
                    label: 'Create Folder',
                    action: function (obj) {
                      const children = $node.children;
                      let text = checkNameDuplicate(children);

                      var newNode = {
                        text: text,
                        children: [],
                        id: 'folder' + tree.sidebar.folders,
                      };
                      let pathString = getPath(newNode, $node);

                      if (
                        $node.parents[$node.parents.length - 2] ===
                        'annotations'
                      ) {
                        tree.sidebar.foldersAnnotationMap.set(
                          newNode.id,
                          pathString
                        );
                      } else if (
                        $node.parents[$node.parents.length - 2] ===
                        'cameraannotations'
                      ) {
                        tree.sidebar.foldersCameraAMap.set(
                          newNode.id,
                          pathString
                        );
                      } else if (
                        $node.parents[$node.parents.length - 2] ===
                        'measurements'
                      ) {
                        tree.sidebar.foldersMeasureMap.set(
                          newNode.id,
                          pathString
                        );
                      }

                      $('#jstree_scene')
                        .jstree()
                        .create_node(
                          $node,
                          newNode,
                          'last',
                          function (new_node) {
                            $('#jstree_scene').jstree('edit', new_node); // Edit node after creation
                          }
                        );

                      tree.sidebar.folders = tree.sidebar.folders + 1;
                    },
                  },
                }
              : false;
          }
        },
      },
    });

    tree.on('move_node.jstree', (e, data) => {
      let result;
      if (
        tree.jstree().last_error().id === 'unique_03' &&
        data.node.id.includes('folder')
      ) {
        result = alert('Do you want to merge folders?');
      }
      if (!result) {
        return;
      }
      let folderMap;
      if (data.node.parents[data.node.parents.length - 2] === 'annotations') {
        folderMap = tree.sidebar.foldersAnnotationMap;
      } else if (
        data.node.parents[data.node.parents.length - 2] === 'cameraannotations'
      ) {
        folderMap = tree.sidebar.foldersCameraAMap;
      } else if (
        data.node.parents[data.node.parents.length - 2] === 'measurements'
      ) {
        folderMap = tree.sidebar.foldersMeasureMap;
      }
      if (data.node.id.includes('folder')) {
        let pathOld = '';
        let pathNew = '';
        if (data.node.id.includes('folder')) {
          if (!data.old_parent.includes('folder')) {
            pathOld = data.node.text;
          } else {
            pathOld = getPath(data.old_parent, null) + '/' + data.node.text;
          }
          if (!data.parent.includes('folder')) {
            pathNew = data.node.text;
          } else {
            pathNew = folderMap.get(data.parent) + '/' + data.node.text;
          }
        } else {
          pathOld = getPath(data.old_parent, null);
          pathNew = folderMap.get(data.parent);
        }

        let folderID = null;
        for (let [key, value] of folderMap.entries()) {
          if (value === pathOld) {
            folderMap.delete(key);
          } else if (value.startsWith(pathOld)) {
            folderMap.set(key, pathNew + value.slice(pathOld.length));
          }
          if (value === pathNew) {
            folderID = key;
          }
        }

        if (tree.jstree().last_error().id === 'unique_03') {
          tree.jstree().last_error().id = null;

          let newParent = $('#jstree_scene').jstree('get_node', folderID);
          for (let i = 0; i < data.node.children.length; i++) {
            const child = $('#jstree_scene').jstree(
              'get_node',
              data.node.children[i]
            );
            $('#jstree_scene').jstree('move_node', child, newParent, 'last');
          }
          tree.jstree('delete_node', data.node);
        } else {
          folderMap.set(data.node.id, pathNew);
        }
      }
    });

    tree.on('rename_node.jstree', (e, data) => {
      if (data.text.includes('/')) {
        tree.viewer.postMessage('Folder names cannot include "/".', {
          duration: 3000,
        });
        $('#jstree_scene').jstree('edit', data.node);
        return;
      }

      // Skip folder-map code for annotation renames (handled in dblclick)
      if (data.node.data instanceof Annotation) {
        return;
      }

      if (viewer.inputHandler) {
        viewer.inputHandler.loadIndexes.set(
          viewer.inputHandler.storageIndex,
          'edit'
        );
        viewer.inputHandler.dispatchEvent({ type: 'save_action' });
      }

      let folderMap;
      if (data.node.parents[data.node.parents.length - 2] === 'annotations') {
        folderMap = tree.sidebar.foldersAnnotationMap;
      } else if (
        data.node.parents[data.node.parents.length - 2] === 'cameraannotations'
      ) {
        folderMap = tree.sidebar.foldersCameraAMap;
      } else if (
        data.node.parents[data.node.parents.length - 2] === 'measurements'
      ) {
        folderMap = tree.sidebar.foldersMeasureMap;
      }

      var node = { text: data.old };
      const pathMain = getPath(node, data.node.parent);
      for (let [key, value] of folderMap.entries()) {
        if (value.startsWith(pathMain)) {
          const node = $('#annotations').jstree('get_node', key);
          const path = getPath(node, node.parent);
          folderMap.set(key, path);
          for (let i = 0; i < node.children.length; i++) {
            if (node.children[i].data) {
              node.children[i].data.folder = path;
            }
          }
        }
      }
    });

    let createNode = (parent, text, icon, object) => {
      let nodeID = tree.jstree(
        'create_node',
        parent,
        {
          text: text,
          icon: icon,
          data: object,
        },
        'last',
        false,
        false
      );
      if (object) {
        if (object.visible) {
          tree.jstree('check_node', nodeID);
        } else {
          tree.jstree('uncheck_node', nodeID);
        }
      }

      return nodeID;
    };

    let pcID = tree.jstree(
      'create_node',
      '#',
      { text: '<b>Point Clouds</b>', id: 'pointclouds' },
      'last',
      false,
      false
    );
    let measurementID = tree.jstree(
      'create_node',
      '#',
      { text: '<b>Measurements</b>', id: 'measurements' },
      'last',
      false,
      false
    );
    let annotationsID = tree.jstree(
      'create_node',
      '#',
      { text: '<b>Annotations</b>', id: 'annotations' },
      'last',
      false,
      false
    );
    let cameraannotationsID = tree.jstree(
      'create_node',
      '#',
      { text: '<b>Camera Positions</b>', id: 'cameraannotations' },
      'last',
      false,
      false
    );
    let otherID = tree.jstree(
      'create_node',
      '#',
      { text: '<b>Other</b>', id: 'other' },
      'last',
      false,
      false
    );
    let vectorsID = tree.jstree(
      'create_node',
      '#',
      { text: '<b>Vectors</b>', id: 'vectors' },
      'last',
      false,
      false
    );
    let imagesID = tree.jstree(
      'create_node',
      '#',
      { text: '<b> Images</b>', id: 'images' },
      'last',
      false,
      false
    );

    tree.jstree('check_node', pcID);
    tree.jstree('check_node', measurementID);
    tree.jstree('check_node', annotationsID);
    tree.jstree('check_node', cameraannotationsID);
    tree.jstree('check_node', otherID);
    tree.jstree('check_node', vectorsID);
    tree.jstree('check_node', imagesID);

    tree.on('create_node.jstree', (e, data) => {
      //tree.jstree('close_all');
    });

    tree.on('select_node.jstree', (e, data) => {
      let object = data.node.data;
      propertiesPanel.set(object);

      this.viewer.inputHandler.deselectAll();

      if (object instanceof Volume) {
        this.viewer.inputHandler.toggleSelection(object);
      }

      $(this.viewer.renderer.domElement).focus();
    });

    tree.on('deselect_node.jstree', (e, data) => {
      propertiesPanel.set(null);
    });

    tree.on('delete_node.jstree', (e, data) => {
      propertiesPanel.set(null);
    });

    tree.on('dblclick', '.jstree-anchor', (e) => {
      let instance = $.jstree.reference(e.target);
      let node = instance.get_node(e.target);
      let object = node.data;

      // ignore double click on checkbox
      if (e.target.classList.contains('jstree-checkbox')) {
        return;
      }

      if (object instanceof PointCloudTree) {
        let box = this.viewer.getBoundingBox([object]);
        let node = new THREE.Object3D();
        node.boundingBox = box;
        this.viewer.zoomTo(node, 1, 500);
      } else if (object instanceof Measure) {
        let points = object.points.map((p) => p.position);
        let box = new THREE.Box3().setFromPoints(points);
        if (box.getSize(new THREE.Vector3()).length() > 0) {
          let node = new THREE.Object3D();
          node.boundingBox = box;
          this.viewer.zoomTo(node, 2, 500);
        } else {
          let target = new THREE.Vector3(
            object.points[0].position.x,
            object.points[0].position.y,
            object.points[0].position.z
          );

          let dir = target
            .clone()
            .sub(this.viewer.scene.view.position)
            .normalize();
          let move = dir.multiplyScalar(2);
          let newCamPos = target.clone().sub(move);

          this.viewer.scene.view.setView(newCamPos, target, 500);
        }
      } else if (object instanceof Profile) {
        let points = object.points;
        let box = new THREE.Box3().setFromPoints(points);
        if (box.getSize(new THREE.Vector3()).length() > 0) {
          let node = new THREE.Object3D();
          node.boundingBox = box;
          this.viewer.zoomTo(node, 1, 500);
        }
      } else if (object instanceof Volume) {
        let box = object.boundingBox.clone().applyMatrix4(object.matrixWorld);

        if (box.getSize(new THREE.Vector3()).length() > 0) {
          let node = new THREE.Object3D();
          node.boundingBox = box;
          this.viewer.zoomTo(node, 1, 500);
        }
      } else if (object instanceof Annotation) {
        // Inline rename: replace anchor text with input
        let anchor = instance.get_node(node, true).children('.jstree-anchor');
        let oldText = object.title;
        let input = $('<input type="text" class="jstree-rename-input" />');
        input.val(oldText);
        input.css({ width: '100%', boxSizing: 'border-box', fontSize: 'inherit', fontFamily: 'inherit' });
        anchor.contents().filter(function() { return this.nodeType === 3; }).first().replaceWith(input);
        input.focus().select();
        let committed = false;
        let commit = () => {
          if (committed) return;
          committed = true;
          let newText = input.val().trim() || oldText;
          object.title = newText;
          input.replaceWith(document.createTextNode(newText));
          instance.rename_node(node, newText);
        };
        input.on('blur', commit);
        input.on('keydown', (ev) => {
          if (ev.key === 'Enter') { ev.preventDefault(); input.blur(); }
          else if (ev.key === 'Escape') { ev.preventDefault(); input.val(oldText); input.blur(); }
          ev.stopPropagation();
        });
      } else if (object) {
        if (object.type === 'SpotLight') {
          let distance = object.distance > 0 ? object.distance / 4 : 5 * 1000;
          let position = object.position;
          let target = new THREE.Vector3().addVectors(
            position,
            object
              .getWorldDirection(new THREE.Vector3())
              .multiplyScalar(distance)
          );

          this.viewer.scene.view.position.copy(object.position);
          this.viewer.scene.view.lookAt(target);
        }
      } else if (object instanceof THREE.Object3D) {
        let box = new THREE.Box3().setFromObject(object);

        if (box.getSize(new THREE.Vector3()).length() > 0) {
          let node = new THREE.Object3D();
          node.boundingBox = box;
          this.viewer.zoomTo(node, 1, 500);
        }
      } else if (object instanceof OrientedImage) {
        // TODO zoom to images
        // let box = new THREE.Box3().setFromObject(object);
        // if(box.getSize(new THREE.Vector3()).length() > 0){
        // 	let node = new THREE.Object3D();
        // 	node.boundingBox = box;
        // 	this.viewer.zoomTo(node, 1, 500);
        // }
      } else if (object instanceof Images360) {
        // TODO
      } //else if (object instanceof Geopackage) {
      // TODO
      //}
    });

    tree.on('uncheck_node.jstree', (e, data) => {
      let object = data.node.data;
      if (data.node.parent === 'images') {
        let imagesTree = tree.jstree(true).get_node('images');
        let visible = [];
        for (let i = 0; i < imagesTree.children.length; i++) {
          if (imagesTree.children[i] === data.node.id) {
            visible.push(false);
          } else {
            visible.push(object.visible[i]);
          }
        }
        object.visible.length = 0;
        object.visible = visible.slice(0);
      } else if (object) {
        object.visible = false;
      }

      for (let child of data.node.children_d) {
        const node = $('#jstree_scene').jstree('get_node', child);
        if (node.data) {
          node.data.visible = false;
        }
      }
    });

    tree.on('check_node.jstree', (e, data) => {
      let object = data.node.data;
      if (data.node.parent === 'images') {
        let imagesTree = tree.jstree(true).get_node('images');
        let visible = [];
        for (let i = 0; i < imagesTree.children.length; i++) {
          if (imagesTree.children[i] === data.node.id) {
            visible.push(true);
          } else {
            visible.push(object.visible[i]);
          }
        }
        object.visible.length = 0;
        object.visible = visible.slice(0);
      } else if (object) {
        object.visible = true;
      }

      for (let child of data.node.children_d) {
        const node = $('#jstree_scene').jstree('get_node', child);
        if (node.data) {
          node.data.visible = true;
        }
      }
    });

    let annotationMoved = (e) => {
      propertiesPanel.set(null);
      propertiesPanel.set(e.annotation);
    };

    this.viewer.scene.addEventListener('annotation_moved', annotationMoved);

    let onPointCloudAdded = (e) => {
      let pointcloud = e.pointcloud;
      let cloudIcon = `${Potree.resourcePath}/icons/cloud.svg`;
      let node = createNode(pcID, pointcloud.name, cloudIcon, pointcloud);

      pointcloud.addEventListener('visibility_changed', () => {
        if (pointcloud.visible) {
          tree.jstree('check_node', node);
        } else {
          tree.jstree('uncheck_node', node);
        }
      });
    };

    let onMeasurementAdded = (e) => {
      let measurement = e.measurement;
      let icon = Utils.getMeasurementIcon(measurement);

      let parentID = '#measurements';

      if (measurement.folder !== null && measurement.folder !== undefined) {
        let measurementRoot = 'measurements';
        let result = parseFolderString(
          measurementRoot,
          measurement,
          this,
          this.foldersMeasureMap
        );
        if (result) {
          parentID = result;
        }
      }

      const title = measurement.name;
      const rootNode = $('#measurements').jstree('get_node', parentID);
      let childArray = [];
      for (let child of rootNode.children) {
        childArray.push($('#measurements').jstree('get_node', child).text);
      }
      for (let i = 0; i <= childArray.length; i++) {
        if (childArray.length === 0) {
          break;
        }

        if (childArray.includes(measurement.name)) {
          measurement.name = title + ' (' + i + ')';
        }
      }

      createNode(parentID, measurement.name, icon, measurement);
    };

    let onVolumeAdded = (e) => {
      let volume = e.volume;
      let icon = Utils.getMeasurementIcon(volume);
      let node = createNode(measurementID, volume.name, icon, volume);

      volume.addEventListener('visibility_changed', () => {
        if (volume.visible) {
          tree.jstree('check_node', node);
        } else {
          tree.jstree('uncheck_node', node);
        }
      });
    };

    let onProfileAdded = (e) => {
      let profile = e.profile;
      let icon = Utils.getMeasurementIcon(profile);
      createNode(measurementID, profile.name, icon, profile);
    };

    let getPath = (node, parentNode) => {
      let pathString = '';
      if (parentNode !== null) {
        let parent = $('#jstree_scene').jstree('get_node', parentNode);
        for (let i = parent.parents.length - 3; i >= 0; i--) {
          pathString +=
            $('#jstree_scene').jstree('get_node', parent.parents[i]).text + '/';
        }
        pathString += parent.text + '/' + node.text;
      } else {
        let currentNode = $('#jstree_scene').jstree('get_node', node);
        const text = currentNode.text;
        for (let i = currentNode.parents.length - 3; i >= 0; i--) {
          pathString +=
            $('#jstree_scene').jstree('get_node', currentNode.parents[i]).text +
            '/';
        }
        pathString += text;
      }
      return pathString;
    };

    let parseFolderString = (root, object, sidebar, folder) => {
      const mainRoot = root;
      let folderMap = folder;
      const pathString = object.folder.text;
      let localFolderArray = String(pathString).split('/');
      let currentPath = '';
      let previousPath = '';
      let node = null;

      for (let [key, value] of folderMap.entries()) {
        if (value === pathString) {
          node = $('#' + mainRoot).jstree('get_node', key);
          break;
        }
      }
      if (!node) {
        for (let line of localFolderArray) {
          previousPath = currentPath;
          if (currentPath.length === 0) {
            currentPath = '' + line;
          } else {
            currentPath = currentPath + '/' + line;
          }
          let path = null;
          for (let [key, value] of folderMap.entries()) {
            if (value === currentPath) {
              path = $('#' + mainRoot).jstree('get_node', key);
              break;
            }
          }
          if (!path) {
            var folderNode = {
              text: line,
              children: [],
              id: 'folder' + sidebar.folders,
            };
            sidebar.folders = sidebar.folders + 1;
            folderMap.set(folderNode.id, currentPath);
            if (object instanceof Annotation) {
              const regex = /[/]/g;
              if (!currentPath.match(regex)) {
                $('#' + mainRoot)
                  .jstree()
                  .create_node($('#' + mainRoot), folderNode, 'last');
                node = $('#' + mainRoot).jstree('get_node', folderNode.id);
              } else {
                for (let [key, value] of folderMap.entries()) {
                  if (value === previousPath) {
                    root = $('#' + mainRoot).jstree('get_node', key);
                    break;
                  }
                }

                $('#' + mainRoot)
                  .jstree()
                  .create_node(root.id, folderNode, 'last');
              }
              node = $('#' + mainRoot).jstree('get_node', folderNode.id);
              tree.jstree('check_node', node);
            }
          }
        }
      }
      if (node !== null) {
        root = $('#' + mainRoot)
          .jstree()
          .get_json(node.id);
        return root.id;
      } else {
        return null;
      }
    };

    let onAnnotationAdded = (e) => {
      let annotation = e.annotation;
      let annotationIcon = `${Potree.resourcePath}/icons/annotation.svg`;

      if (!annotation.cameraPosition) {
        let parentID = this.annotationMapping.get(annotation.parent);

        if (annotation.folder !== null && annotation.folder !== undefined) {
          const annotationsRoot = 'annotations';
          const result = parseFolderString(
            annotationsRoot,
            annotation,
            this,
            this.foldersAnnotationMap
          );
          if (result) {
            parentID = result;
          }
        }

        const title = annotation.title;
        const rootNode = $('#annotations').jstree('get_node', parentID);
        let childArray = [];
        for (let child of rootNode.children) {
          childArray.push($('#annotations').jstree('get_node', child).text);
        }
        for (let i = 0; i <= childArray.length; i++) {
          if (childArray.length === 0) {
            break;
          }

          if (childArray.includes(annotation.title)) {
            annotation.title = title + ' (' + i + ')';
          }
        }

        let annotationID = createNode(
          parentID,
          annotation.title,
          annotationIcon,
          annotation
        );
        this.annotationMapping.set(annotation, annotationID);
      } else {
        let parentID = 'cameraannotations';

        if (annotation.folder !== null && annotation.folder !== undefined) {
          let annotationsRoot = 'cameraannotations';

          let result = parseFolderString(
            annotationsRoot,
            annotation,
            this,
            this.foldersCameraAMap
          );
          if (result) {
            parentID = result;
          }
        }

        const title = annotation.title;
        const rootNode = $('#cameraannotations').jstree('get_node', parentID);
        let childArray = [];
        for (let child of rootNode.children) {
          childArray.push(
            $('#cameraannotations').jstree('get_node', child).text
          );
        }
        for (let i = 0; i <= childArray.length; i++) {
          if (childArray.length === 0) {
            break;
          }

          if (childArray.includes(annotation.title)) {
            annotation.title = title + ' (' + i + ')';
          }
        }

        // Ensure title is a string (jstree requires string for toLowerCase)
        let titleText = typeof annotation.title === 'string' 
          ? annotation.title 
          : (annotation.title?.text?.() || annotation.title?.toString?.() || String(annotation.title));
        
        let cameraannotationsID = createNode(
          parentID,
          titleText,
          annotationIcon,
          annotation
        );
        this.annotationMapping.set(annotation, cameraannotationsID);
      }
      annotation.addEventListener('annotation_changed', (e) => {
        let jsonNode = findJsonNodeByUuid('annotations', annotation.uuid);

        if (jsonNode === undefined || jsonNode === null) {
          let annotationsRoot = $('#jstree_scene')
            .jstree()
            .get_json('cameraannotations');
          jsonNode = annotationsRoot.children?.find(
            (child) => child.data.uuid === annotation.uuid
          );
        }

        if (jsonNode) {
          $.jstree
            .reference(jsonNode.id)
            .rename_node(jsonNode.id, annotation.title);
        }
      });
    };

    let onCameraAnimationAdded = (e) => {
      const animation = e.animation;

      const animationIcon = `${Potree.resourcePath}/icons/camera_animation.svg`;
      createNode(otherID, 'animation', animationIcon, animation);
    };

    let onOrientedImagesAdded = (e) => {
      const images = e.images;

      const imagesIcon = `${Potree.resourcePath}/icons/picture.svg`;
      const node = createNode(imagesID, 'images', imagesIcon, images);

      images.addEventListener('visibility_changed', () => {
        if (images.visible) {
          tree.jstree('check_node', node);
        } else {
          tree.jstree('uncheck_node', node);
        }
      });
    };

    let onImages360Added = (e) => {
      const images = e.images;
      tree.jstree('open_all');
      var nodeMain = $('#images');
      var children = $('#images').jstree('get_children_dom', nodeMain);

      tree.jstree('delete_node', children);
      tree.jstree('close_all');
      const imagesIcon = `${Potree.resourcePath}/icons/picture.svg`;
      let imagesSourceUnique = [];
      for (let img360 of scene.images360) {
        const label = img360.displayName || null;
        if (label) {
          if (!imagesSourceUnique.includes(label)) {
            imagesSourceUnique.push(label);
          }
        } else {
          for (const img of img360.images) {
            const n = img.name || 'coordinates';
            if (!imagesSourceUnique.includes(n)) {
              imagesSourceUnique.push(n);
            }
          }
        }
      }
      if (imagesSourceUnique.includes('coordinates')) {
        let index = imagesSourceUnique.indexOf('coordinates');
        imagesSourceUnique[index] = '360\u00b0 images';
      }

      function visiblilityEvent(event, node) {
        if (images.visible[index]) {
          tree.jstree('check_node', node);
        } else {
          tree.jstree('uncheck_node', node);
        }
      }

      for (var [key, value] of Object.entries(images._listeners)) {
        if (String(key) === 'visibility_changed') {
          if (value.length < imagesSourceUnique.length) {
            images.removeEventListener('visibility_changed', visiblilityEvent);
          }
        }
      }

      let index = 0;
      for (let imagesSource of imagesSourceUnique) {
        let node = createNode(imagesID, imagesSource, imagesIcon, images);
        images.addEventListener('visibility_changed', visiblilityEvent(node));
        index++;
      }
    };

    const onGeopackageAdded = (e) => {
      const geopackage = e.geopackage;

      const geopackageIcon = `${Potree.resourcePath}/icons/triangle.svg`;
      const tree = $(`#jstree_scene`);
      const parentNode = 'vectors';

      for (const layer of geopackage.node.children) {
        const name = layer.name;

        let shpPointsID = tree.jstree(
          'create_node',
          parentNode,
          {
            text: name,
            icon: geopackageIcon,
            object: layer,
            data: layer,
          },
          'last',
          false,
          false
        );
        tree.jstree(layer.visible ? 'check_node' : 'uncheck_node', shpPointsID);
      }
    };

    this.viewer.scene.addEventListener('pointcloud_added', onPointCloudAdded);
    this.viewer.scene.addEventListener('measurement_added', onMeasurementAdded);
    this.viewer.scene.addEventListener('profile_added', onProfileAdded);
    this.viewer.scene.addEventListener('volume_added', onVolumeAdded);
    this.viewer.scene.addEventListener(
      'camera_animation_added',
      onCameraAnimationAdded
    );
    this.viewer.scene.addEventListener(
      'oriented_images_added',
      onOrientedImagesAdded
    );
    this.viewer.scene.addEventListener('360_images_added', onImages360Added);
    this.viewer.scene.addEventListener('geopackage_added', onGeopackageAdded);
    this.viewer.scene.annotations.addEventListener(
      'annotation_added',
      onAnnotationAdded
    );

    let onMeasurementRemoved = (e) => {
      // Try to get the node by UUID directly
      let node = $('#jstree_scene').jstree('get_node', e.measurement.uuid);

      if (node) {
        tree.jstree('delete_node', node);
        return;
      }

      // Fallback: search by data.uuid if not found by id
      let jsonNode = findJsonNodeByUuid('measurements', e.measurement.uuid);
      if (jsonNode) {
        tree.jstree('delete_node', jsonNode.id);
      }
    };

    let onVolumeRemoved = (e) => {
      let jsonNode = findJsonNodeByUuid('measurements', e.volume.uuid);

      tree.jstree('delete_node', jsonNode.id);
    };

    let onProfileRemoved = (e) => {
      let jsonNode = findJsonNodeByUuid('measurements', e.profile.uuid);

      tree.jstree('delete_node', jsonNode.id);
    };

    let onCameraAnimationRemoved = (e) => {
      let jsonNode = findJsonNodeByUuid('other', e.animation.uuid);
      tree.jstree('delete_node', jsonNode.id);
    };

    let onAnnotationRemoved = (e) => {
      let jsonNode = findJsonNodeByUuid('annotations', e.annotation.uuid);
      tree.jstree('delete_node', jsonNode);
    };

    this.viewer.scene.addEventListener(
      'measurement_removed',
      onMeasurementRemoved
    );
    this.viewer.scene.addEventListener('volume_removed', onVolumeRemoved);
    this.viewer.scene.addEventListener('profile_removed', onProfileRemoved);
    this.viewer.scene.annotations.addEventListener(
      'annotation_removed',
      onAnnotationRemoved
    );

    this.viewer.scene.addEventListener(
      'camera_animation_removed',
      onCameraAnimationRemoved
    );

    //Load annotations from project
    {
      this.annotationMapping = new Map();
      this.annotationMapping.set(this.viewer.scene.annotations, annotationsID);
      this.viewer.scene.annotations.traverseDescendants((annotation) => {
        this.viewer.scene.annotations.dispatchEvent({
          type: 'annotation_added',
          annotation: annotation,
        });
      });
    }

    const scene = this.viewer.scene;
    for (let pointcloud of scene.pointclouds) {
      onPointCloudAdded({ pointcloud: pointcloud });
    }

    for (let measurement of scene.measurements) {
      onMeasurementAdded({ measurement: measurement });
    }

    for (let volume of scene.volumes) {
      onVolumeAdded({ volume: volume });
    }

    for (let animation of scene.cameraAnimations) {
      onCameraAnimationAdded({ animation: animation });
    }

    for (let images of scene.orientedImages) {
      onOrientedImagesAdded({ images: images });
    }

    /*for (let images of scene.images360) {
      onImages360Added({ images: images });
    }*/

    let imagesSourceUnique = [];
    for (let images of scene.images360) {
      if (!imagesSourceUnique.includes(images.images.name)) {
        imagesSourceUnique.push(images.images.name);
      }
    }

    for (let imagesSource of imagesSourceUnique) {
      let tempImages = [];
      for (let image of scene.images360.images) {
        if (image.name === imagesSource) {
          tempImages.push(image);
        }
      }
      onImages360Added({ tempImages: tempImages });
    }

    for (const geopackage of scene.geopackages) {
      onGeopackageAdded({ geopackage: geopackage });
    }

    for (let profile of scene.profiles) {
      onProfileAdded({ profile: profile });
    }

    {
      createNode(otherID, 'Camera', null, new THREE.Camera());
    }

    this.viewer.addEventListener('scene_changed', (e) => {
      propertiesPanel.setScene(e.scene);

      e.oldScene.removeEventListener('pointcloud_added', onPointCloudAdded);
      e.oldScene.removeEventListener('measurement_added', onMeasurementAdded);
      e.oldScene.removeEventListener('profile_added', onProfileAdded);
      e.oldScene.removeEventListener('volume_added', onVolumeAdded);
      e.oldScene.removeEventListener(
        'polygon_clip_volume_added',
        onVolumeAdded
      );
      e.oldScene.removeEventListener(
        'measurement_removed',
        onMeasurementRemoved
      );
      e.oldScene.annotations.removeEventListener(
        'annotation_removed',
        onAnnotationRemoved
      );

      e.scene.addEventListener('pointcloud_added', onPointCloudAdded);
      e.scene.addEventListener('measurement_added', onMeasurementAdded);
      e.scene.addEventListener('profile_added', onProfileAdded);
      e.scene.addEventListener('volume_added', onVolumeAdded);
      e.scene.addEventListener('measurement_removed', onMeasurementRemoved);
      e.scene.annotations.addEventListener(
        'annotation_removed',
        onAnnotationRemoved
      );
    });
  }

  initClippingTool() {
    // Kept for compatibility — clipping is now initialized in initToolbar
  }

  initFilters() {
    this.initClassificationList();
    this.initReturnFilters();
    this.initGPSTimeFilters();
    this.initPointSourceIDFilters();
  }

  initReturnFilters() {
    let elReturnFilterPanel = $('#return_filter_panel');

    {
      // RETURN NUMBER
      let sldReturnNumber = elReturnFilterPanel.find('#sldReturnNumber');
      let lblReturnNumber = elReturnFilterPanel.find('#lblReturnNumber');

      sldReturnNumber.slider({
        range: true,
        min: 0,
        max: 7,
        step: 1,
        values: [0, 7],
        slide: (event, ui) => {
          this.viewer.setFilterReturnNumberRange(ui.values[0], ui.values[1]);
        },
      });

      let onReturnNumberChanged = (event) => {
        let [from, to] = this.viewer.filterReturnNumberRange;

        lblReturnNumber[0].innerHTML = `${from} to ${to}`;
        sldReturnNumber.slider({ values: [from, to] });
      };

      this.viewer.addEventListener(
        'filter_return_number_range_changed',
        onReturnNumberChanged
      );

      onReturnNumberChanged();
    }

    {
      // NUMBER OF RETURNS
      let sldNumberOfReturns = elReturnFilterPanel.find('#sldNumberOfReturns');
      let lblNumberOfReturns = elReturnFilterPanel.find('#lblNumberOfReturns');

      sldNumberOfReturns.slider({
        range: true,
        min: 0,
        max: 7,
        step: 1,
        values: [0, 7],
        slide: (event, ui) => {
          this.viewer.setFilterNumberOfReturnsRange(ui.values[0], ui.values[1]);
        },
      });

      let onNumberOfReturnsChanged = (event) => {
        let [from, to] = this.viewer.filterNumberOfReturnsRange;

        lblNumberOfReturns[0].innerHTML = `${from} to ${to}`;
        sldNumberOfReturns.slider({ values: [from, to] });
      };

      this.viewer.addEventListener(
        'filter_number_of_returns_range_changed',
        onNumberOfReturnsChanged
      );

      onNumberOfReturnsChanged();
    }
  }

  initGPSTimeFilters() {
    let elGPSTimeFilterPanel = $('#gpstime_filter_panel');

    {
      let slider = new HierarchicalSlider({
        levels: 4,
        slide: (event) => {
          this.viewer.setFilterGPSTimeRange(...event.values);
        },
      });

      let initialized = false;

      let initialize = () => {
        let elRangeContainer = $('#gpstime_multilevel_range_container');
        elRangeContainer[0].prepend(slider.element);

        let extent = this.viewer.getGpsTimeExtent();

        slider.setRange(extent);
        slider.setValues(extent);

        initialized = true;
      };

      this.viewer.addEventListener('update', (e) => {
        let extent = this.viewer.getGpsTimeExtent();
        let gpsTimeAvailable = extent[0] !== Infinity;

        if (!initialized && gpsTimeAvailable) {
          initialize();
        }

        slider.setRange(extent);
      });
    }

    {
      const txtGpsTime = elGPSTimeFilterPanel.find('#txtGpsTime');
      const btnFindGpsTime = elGPSTimeFilterPanel.find('#btnFindGpsTime');

      let targetTime = null;

      txtGpsTime.on('input', (e) => {
        const str = txtGpsTime.val();

        if (!isNaN(str)) {
          const value = parseFloat(str);
          targetTime = value;

          txtGpsTime.css('background-color', '');
        } else {
          targetTime = null;

          txtGpsTime.css('background-color', '#ff9999');
        }
      });

      btnFindGpsTime.click(() => {
        if (targetTime !== null) {
          viewer.moveToGpsTimeVicinity(targetTime);
        }
      });
    }
  }

  initPointSourceIDFilters() {
    let elPointSourceIDFilterPanel = $('#pointsourceid_filter_panel');

    {
      let slider = new HierarchicalSlider({
        levels: 4,
        range: [0, 65535],
        precision: 1,
        slide: (event) => {
          let values = event.values;
          this.viewer.setFilterPointSourceIDRange(values[0], values[1]);
        },
      });

      let initialized = false;

      let initialize = () => {
        elPointSourceIDFilterPanel[0].prepend(slider.element);

        initialized = true;
      };

      this.viewer.addEventListener('update', (e) => {
        let extent = this.viewer.filterPointSourceIDRange;

        if (!initialized) {
          initialize();

          slider.setValues(extent);
        }
      });
    }

    // let lblPointSourceID = elPointSourceIDFilterPanel.find("#lblPointSourceID");
    // let elPointSourceID = elPointSourceIDFilterPanel.find("#spnPointSourceID");

    // let slider = new ZoomableSlider();
    // elPointSourceID[0].appendChild(slider.element);
    // slider.update();

    // slider.change( () => {
    // 	let range = slider.chosenRange;
    // 	this.viewer.setFilterPointSourceIDRange(range[0], range[1]);
    // });

    // let onPointSourceIDExtentChanged = (event) => {
    // 	let range = this.viewer.filterPointSourceIDExtent;
    // 	slider.setVisibleRange(range);
    // };

    // let onPointSourceIDChanged = (event) => {
    // 	let range = this.viewer.filterPointSourceIDRange;

    // 	let precision = 1;
    // 	let from = `${Utils.addCommas(range[0].toFixed(precision))}`;
    // 	let to = `${Utils.addCommas(range[1].toFixed(precision))}`;
    // 	lblPointSourceID[0].innerHTML = `${from} to ${to}`;

    // 	slider.setRange(range);
    // };

    // this.viewer.addEventListener('filter_point_source_id_range_changed', onPointSourceIDChanged);
    // this.viewer.addEventListener('filter_point_source_id_extent_changed', onPointSourceIDExtentChanged);
  }

  initClassificationList() {
    let elClassificationList = $('#classificationList');

    let addClassificationItem = (code, name) => {
      const classification = this.viewer.classifications[code];
      const inputID = 'chkClassification_' + code;
      const colorPickerID = 'colorPickerClassification_' + code;

      const checked = classification.visible ? 'checked' : '';

      let element = $(`
				<li>
					<label style="whitespace: nowrap; display: flex">
						<input id="${inputID}" type="checkbox" ${checked}/>
						<span style="flex-grow: 1">${name}</span>
						<input id="${colorPickerID}" style="zoom: 0.5" />
					</label>
				</li>
			`);

      const elInput = element.find('input');
      const elColorPicker = element.find(`#${colorPickerID}`);

      elInput.click((event) => {
        this.viewer.setClassificationVisibility(code, event.target.checked);
      });

      let defaultColor = classification.color.map((c) => c * 255).join(', ');
      defaultColor = `rgb(${defaultColor})`;

      elColorPicker.spectrum({
        // flat: true,
        color: defaultColor,
        showInput: true,
        preferredFormat: 'rgb',
        cancelText: '',
        chooseText: 'Apply',
        move: (color) => {
          let rgb = color.toRgb();
          const c = [rgb.r / 255, rgb.g / 255, rgb.b / 255, 1];
          classification.color = c;
        },
        change: (color) => {
          let rgb = color.toRgb();
          const c = [rgb.r / 255, rgb.g / 255, rgb.b / 255, 1];
          classification.color = c;
        },
      });

      elClassificationList.append(element);
    };

    const addToggleAllButton = () => {
      // toggle all button
      const element = $(`
				<li>
					<label style="whitespace: nowrap">
						<input id="toggleClassificationFilters" type="checkbox" checked/>
						<span>show/hide all</span>
					</label>
				</li>
			`);

      let elInput = element.find('input');

      elInput.click((event) => {
        this.viewer.toggleAllClassificationsVisibility();
      });

      elClassificationList.append(element);
    };

    const addInvertButton = () => {
      const element = $(`
				<li>
					<input type="button" value="invert" />
				</li>
			`);

      let elInput = element.find('input');

      elInput.click(() => {
        const classifications = this.viewer.classifications;

        for (let key of Object.keys(classifications)) {
          let value = classifications[key];
          this.viewer.setClassificationVisibility(key, !value.visible);
        }
      });

      elClassificationList.append(element);
    };

    const populate = () => {
      addToggleAllButton();
      for (let classID in this.viewer.classifications) {
        addClassificationItem(
          classID,
          this.viewer.classifications[classID].name
        );
      }
      addInvertButton();
    };

    populate();

    this.viewer.addEventListener('classifications_changed', () => {
      elClassificationList.empty();
      populate();
    });

    this.viewer.addEventListener('classification_visibility_changed', () => {
      {
        // set checked state of classification buttons
        for (const classID of Object.keys(this.viewer.classifications)) {
          const classValue = this.viewer.classifications[classID];

          let elItem = elClassificationList.find(
            `#chkClassification_${classID}`
          );
          elItem.prop('checked', classValue.visible);
        }
      }

      {
        // set checked state of toggle button based on state of all other buttons
        let numVisible = 0;
        let numItems = 0;
        for (const key of Object.keys(this.viewer.classifications)) {
          if (this.viewer.classifications[key].visible) {
            numVisible++;
          }
          numItems++;
        }
        const allVisible = numVisible === numItems;

        let elToggle = elClassificationList.find(
          '#toggleClassificationFilters'
        );
        elToggle.prop('checked', allVisible);
      }
    });
  }

  initAccordion() {
    $('.accordion > h3').each(function () {
      let header = $(this);
      let content = $(this).next();

      content.addClass('accordion-content');

      // Start collapsed unless marked as expanded
      if (!header.hasClass('expanded')) {
        content.hide();
      }

      header.click(() => {
        header.toggleClass('expanded');
        content.slideToggle(200);
      });
    });

  }

  initAppearance() {
    const sldPointBudget = this.dom.find('#sldPointBudget');

    sldPointBudget.slider({
      value: this.viewer.getPointBudget(),
      min: 100 * 1000,
      max: 10 * 1000 * 1000,
      step: 1000,
      slide: (event, ui) => {
        this.viewer.setPointBudget(ui.value);
      },
    });

    this.dom.find('#sldFOV').slider({
      value: this.viewer.getFOV(),
      min: 20,
      max: 100,
      step: 1,
      slide: (event, ui) => {
        this.viewer.setFOV(ui.value);
      },
    });

    $('#sldEDLRadius').slider({
      value: this.viewer.getEDLRadius(),
      min: 1,
      max: 4,
      step: 0.01,
      slide: (event, ui) => {
        this.viewer.setEDLRadius(ui.value);
      },
    });

    $('#sldEDLStrength').slider({
      value: this.viewer.getEDLStrength(),
      min: 0,
      max: 5,
      step: 0.01,
      slide: (event, ui) => {
        this.viewer.setEDLStrength(ui.value);
      },
    });

    $('#sldEDLOpacity').slider({
      value: this.viewer.getEDLOpacity(),
      min: 0,
      max: 1,
      step: 0.01,
      slide: (event, ui) => {
        this.viewer.setEDLOpacity(ui.value);
      },
    });

    this.viewer.addEventListener('point_budget_changed', (event) => {
      $('#lblPointBudget')[0].innerHTML = Utils.addCommas(
        this.viewer.getPointBudget()
      );
      sldPointBudget.slider({ value: this.viewer.getPointBudget() });
    });

    this.viewer.addEventListener('fov_changed', (event) => {
      $('#lblFOV')[0].innerHTML = parseInt(this.viewer.getFOV());
      $('#sldFOV').slider({ value: this.viewer.getFOV() });
    });

    this.viewer.addEventListener('use_edl_changed', (event) => {
      $('#chkEDLEnabled')[0].checked = this.viewer.getEDLEnabled();
    });

    this.viewer.addEventListener('edl_radius_changed', (event) => {
      $('#lblEDLRadius')[0].innerHTML = this.viewer.getEDLRadius().toFixed(1);
      $('#sldEDLRadius').slider({ value: this.viewer.getEDLRadius() });
    });

    this.viewer.addEventListener('edl_strength_changed', (event) => {
      $('#lblEDLStrength')[0].innerHTML = this.viewer
        .getEDLStrength()
        .toFixed(1);
      $('#sldEDLStrength').slider({ value: this.viewer.getEDLStrength() });
    });

    this.viewer.addEventListener('background_changed', (event) => {
      $(
        "input[name=background][value='" + this.viewer.getBackground() + "']"
      ).prop('checked', true);
    });

    $('#lblPointBudget')[0].innerHTML = Utils.addCommas(
      this.viewer.getPointBudget()
    );
    $('#lblFOV')[0].innerHTML = parseInt(this.viewer.getFOV());
    $('#lblEDLRadius')[0].innerHTML = this.viewer.getEDLRadius().toFixed(1);
    $('#lblEDLStrength')[0].innerHTML = this.viewer.getEDLStrength().toFixed(1);
    $('#chkEDLEnabled')[0].checked = this.viewer.getEDLEnabled();

    {
      let elBackground = $(`#background_options`);
      elBackground.selectgroup();

      elBackground.find('input').click((e) => {
        this.viewer.setBackground(e.target.value);
      });

      let currentBackground = this.viewer.getBackground();
      $(`input[name=background_options][value=${currentBackground}]`).trigger(
        'click'
      );
    }

    $('#chkEDLEnabled').click(() => {
      this.viewer.setEDLEnabled($('#chkEDLEnabled').prop('checked'));
    });
  }

  initNavigation() {
    // Kept for compatibility — navigation is now initialized in initToolbar
  }

  initSettings() {
    {
      $('#sldMinNodeSize').slider({
        value: this.viewer.getMinNodeSize(),
        min: 0,
        max: 1000,
        step: 0.01,
        slide: (event, ui) => {
          this.viewer.setMinNodeSize(ui.value);
        },
      });

      this.viewer.addEventListener('minnodesize_changed', (event) => {
        $('#lblMinNodeSize').html(parseInt(this.viewer.getMinNodeSize()));
        $('#sldMinNodeSize').slider({ value: this.viewer.getMinNodeSize() });
      });
      $('#lblMinNodeSize').html(parseInt(this.viewer.getMinNodeSize()));
    }

    this.viewer.useHQ = false;

    $('#show_bounding_box').prop('checked', this.viewer.getShowBoundingBox());
    $('#show_bounding_box').click(() => {
      this.viewer.setShowBoundingBox($('#show_bounding_box').prop('checked'));
    });
    this.viewer.addEventListener('show_boundingbox_changed', () => {
      $('#show_bounding_box').prop('checked', this.viewer.getShowBoundingBox());
    });

    $('#set_freeze').prop('checked', this.viewer.freeze);
    $('#set_freeze').click(() => {
      this.viewer.setFreeze($('#set_freeze').prop('checked'));
    });
  }
}
